'use client'

import { useState, useEffect, useLayoutEffect, useRef, useCallback, useId, useMemo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { isChatThreadsV2Enabled } from '@/lib/connectors/flags'
import { ChatThreadSidebar } from '@/components/chat/ChatThreadSidebar'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import { QuoteDownIcon, PencilEdit01Icon } from '@hugeicons-pro/core-solid-rounded'
import { ArrowDown02Icon, SidebarRightIcon, Bookmark02Icon, MoreHorizontalIcon, ArrowShrink02Icon, ArrowExpand02Icon, Menu01Icon, Cancel01Icon, ArrowDown01Icon, Download01Icon, ArrowLeft01Icon, SearchIcon, FlashIcon } from '@hugeicons-pro/core-stroke-rounded'
import { Switch } from '@/components/ui/switch'
import { useChat } from '@/contexts/ChatContext'
import { useCachedUser } from '@/hooks/useCachedUser'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { playSendSound, playReceiveSound } from '@/lib/sounds'
import { requestUserChatActivityRefresh } from '@/lib/user-chat-activity'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Logo } from '@/components/Logo'
// PageStateContextBar import removed — context bar hidden per Stephen's feedback
import type { ToolCallState } from '@/components/chat/ToolCallCard'
import { ChatPromptBox } from '@/components/chat/ChatPromptBox'
import type { SlashCommandUiAction } from '@/lib/slash-commands'
import type { MessageSegment } from '@/components/chat/messageSegment'
import { ChatUserMessage } from '@/components/chat/ChatUserMessage'
import { ChatAssistantMessage } from '@/components/chat/ChatAssistantMessage'
import { assistantMessageTimeShort } from '@/components/chat/assistantMessageTime'
import { sendUAConversion, type UAEventName } from '@/lib/measurement/ua-client'
import { createClient } from '@/utils/supabase/client'
import { useMarketingAnimatedPlaceholder } from '@/components/marketing/useMarketingAnimatedPlaceholder'
import { useUsageLimitHandler, type UsageLimitDetails } from '@/hooks/use-usage-limit-handler'
import {
  CHAT_COLUMN_CLASS,
  CHAT_EMPTY_STATE_CLASS,
  CHAT_MESSAGE_NARROW_CLASS,
  CHAT_PANEL_COMPOSER_CLASS,
} from '@/lib/chat-layout'

export type { MessageSegment } from '@/components/chat/messageSegment'

export interface ChatMessage {
  id?: string // DB message ID (only for persisted/hydrated messages)
  role: 'user' | 'assistant' | 'tool' | 'system'
  content: string
  isTyping?: boolean
  isDivider?: boolean // session divider — horizontal rule between sessions
  options?: string[]
  toolKey?: string // for role === 'tool' messages
  channel?: string // 'web' | 'telegram' | 'slack' | 'imessage' | 'email'
  created_at?: string // for timestamp display
  segments?: MessageSegment[] // ordered text/tool interleaving (client-side only)
  thinkingText?: string // accumulated thinking text (client-side only)
  /** Quoted text snippets shown as a styled block above the user message bubble (client-side only). */
  quotedTexts?: Array<{ text: string; messageIndex?: number }>
  /** File attachments displayed above the message bubble. */
  attachments?: Array<{ id: string; file_name: string; file_type: string; file_size: number }>
  /** When true, show a retry button that re-sends retryMessage. */
  isRetryable?: boolean
  retryMessage?: string
}

interface ThreadItem {
  id: string
  title: string
  flow_intent: string | null
  updated_at: string
  last_message: string | null
  model?: string | null
}

export const MODEL_OPTIONS = [
  { label: 'Auto', value: 'auto', description: 'Automatically picks the best model' },
  { label: 'Opus 4.7', value: 'claude-opus-4-7', description: 'Most capable — strategy & complex reasoning' },
  { label: 'Sonnet 4.6', value: 'claude-sonnet-4-6', description: 'Fast & efficient — simple queries' },
  { label: 'GPT 5.5', value: 'gpt-5.5', description: 'OpenAI latest' },
  { label: 'Gemini 3.1', value: 'gemini-3.1-pro', description: 'Google latest', disabled: true, comingSoon: true },
]

type ChatToolMode = 'ask' | 'agent'

function isUsageLimitDetails(value: unknown): value is UsageLimitDetails {
  if (!value || typeof value !== 'object') return false
  const details = value as Record<string, unknown>
  return (
    typeof details.eventType === 'string' &&
    typeof details.creditsRequired === 'number' &&
    typeof details.creditsAvailable === 'number' &&
    typeof details.upgradeUrl === 'string'
  )
}

const CHAT_MODE_STORAGE_KEY = 'atrium.chat.mode'
const CHAT_MODE_OPTIONS: Array<{
  label: string
  value: ChatToolMode
  description: string
  tooltip: string
  icon: typeof SearchIcon
}> = [
  {
    label: 'Ask',
    value: 'ask',
    description: 'Read-only answers',
    tooltip: "Read-only. Chat researches and answers but won't make changes.",
    icon: SearchIcon,
  },
  {
    label: 'Agent',
    value: 'agent',
    description: 'Full tool access',
    tooltip: 'Full access. Chat can create tasks, send messages, update state.',
    icon: FlashIcon,
  },
]

function isChatToolMode(value: string | null): value is ChatToolMode {
  return value === 'ask' || value === 'agent'
}

export interface ChatInterfaceProps {
  // API
  endpoint: string
  endpointBody?: Record<string, unknown>
  workspaceId?: string | null

  // Behavior
  flowIntent?: string | null
  seedMessage?: string | null
  /** When true with seedMessage, sends the seed as the first user message once the thread is ready. */
  autoSendSeed?: boolean
  onAutoSendConsumed?: () => void
  /** Server-side assistant-first onboarding kickoff stream. */
  onboardingKickoffEndpoint?: string | null
  threadId?: string | null
  onThreadCreated?: (threadId: string, title: string) => void
  forceNewThread?: boolean // deprecated — single conversation mode, ignored

  // UI events from stream
  onNavigate?: (url: string) => void
  onUiEvent?: (event: Record<string, unknown>) => void

  // Message transform — applied to API payload only (user sees original)
  apiMessageTransform?: (userMessage: string) => string

  // Idle extraction
  extractEndpoint?: string | null

  // Guards rendering until workspace context is ready
  workspaceReady?: boolean

  // Layout
  variant?: 'panel' | 'fullpage' | 'fullscreen'
  /** Extra actions shown in the More options menu (used by fullscreen variant for skip/logout/terms). */
  headerActions?: Array<{ label: string; onClick?: () => void; href?: string }>
  className?: string

  // Optional right panel slot (rendered beside the chat in fullpage mode)
  rightPanel?: React.ReactNode

  // Legacy slash command handler
  onSlashCommand?: (intent: string) => void

  // Memory indicator
  hasMemory?: boolean

  // Field context quotes rendered above the input (moved here from the parent wrapper)
  fieldContexts?: Array<{ field: string; currentValue?: string | null }>
  onDismissFieldContexts?: () => void

  /** When false, skip panel-only UI (e.g. auto-send, input focus) while the shell keeps the panel mounted. Thread state stays in memory. */
  panelActive?: boolean
  /** Collapse the docked chat panel (header control). */
  onPanelCollapse?: () => void
  /** Renders at the top of the panel (inside the floating card), e.g. field context banner from CampaignChatPanel. */
  panelFrameTop?: ReactNode
  /** Called when the user clicks the collapse/minimize button in fullscreen mode. */
  onCollapse?: () => void
  /** Called when the user clicks the menu button in fullscreen mode. */
  onMenuToggle?: () => void
  /**
   * When set with `panelViewportFullscreen`, the header expand control toggles viewport-wide
   * overlay fullscreen in the parent shell instead of navigating to `/chat`.
   */
  panelViewportFullscreen?: boolean
  onPanelViewportFullscreenChange?: (fullscreen: boolean) => void
  /**
   * When true, render the mobile-flavored fullscreen layout:
   *  - static "Plan, Launch..." placeholder instead of rotating marketing copy
   *  - chevron-left back button instead of the fullscreen-shrink icon (uses onCollapse)
   *  - input footer paints bg-atrium-chatbar so it visually merges with the input
   *  - hides the desktop logo/menu cluster (no sidebar to toggle on mobile)
   * Only meaningful when variant === 'fullscreen'.
   */
  mobileLayout?: boolean

}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = now - then
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

function intentBadge(intent: string | null): string | null {
  const labels: Record<string, string> = {
    campaign: 'Campaign',
    video: 'Video',
    automation: 'Automation',
    brand: 'Brand',
  }
  return intent ? labels[intent] || null : null
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ChatInterface({
  endpoint,
  endpointBody,
  workspaceId,
  flowIntent,
  seedMessage,
  autoSendSeed = false,
  onAutoSendConsumed,
  onboardingKickoffEndpoint,
  threadId: externalThreadId,
  onThreadCreated,
  forceNewThread,
  onNavigate,
  onUiEvent,
  apiMessageTransform,
  extractEndpoint,
  workspaceReady = true,
  variant = 'panel',
  className,
  rightPanel,
  hasMemory,
  fieldContexts,
  onDismissFieldContexts,
  panelActive = true,
  onPanelCollapse,
  panelFrameTop,
  headerActions,
  onCollapse,
  onMenuToggle,
  panelViewportFullscreen = false,
  onPanelViewportFullscreenChange,
  mobileLayout = false,
}: ChatInterfaceProps) {
  const router = useRouter()
  const { showUsageLimit } = useUsageLimitHandler()
  // Operation Sherman Phase 1 — thread-based chat. When enabled:
  //  - getOrCreateThread does NOT auto-pick the latest thread; the user
  //    starts in a fresh "new chat" state and picks from the sidebar.
  //  - Message hydration is thread-only (no workspaceId-wide fetch).
  //  - After the first assistant reply on a fresh thread, an LLM title
  //    is generated via /api/chat/threads/:id/auto-title.
  //  - Session dividers are not woven into the message list (each thread
  //    renders alone).
  // Flag: NEXT_PUBLIC_SHERMAN_CHAT_THREADS_V2=1
  const chatThreadsV2 = useMemo(() => isChatThreadsV2Enabled(), [])
  const { godMode, setGodMode, setPendingSonobiQuery, droppedFile, setDroppedFile, pendingScrollToMessageId, setPendingScrollToMessageId, flagUrlChangeSource } = useChat()
  const { user: currentUser } = useCachedUser()
  const isAdmin = !!(currentUser?.email?.endsWith('@atrium.run') || currentUser?.email?.endsWith('@atrium.bot') || currentUser?.email?.endsWith('@atrium.watch'))
  const slashCommandSupabase = useMemo(() => createClient(), [])
  // `slashCommandUiHooksRef` lets the slash command context call `startNewSession`
  // / `stopStreaming` / etc. without re-creating the context every render. The ref
  // is repopulated in an effect below once those callbacks are defined.
  const slashCommandUiHooksRef = useRef<{
    startNewThread?: () => void
    stopStreaming?: () => void
    submitSideQuestion?: (prompt: string) => void
  }>({})
  const slashCommandContext = useMemo(
    () => (currentUser?.id && workspaceId
      ? {
          workspaceId,
          userId: currentUser.id,
          router,
          supabase: slashCommandSupabase,
          isAdmin,
          ui: {
            startNewThread: () => slashCommandUiHooksRef.current.startNewThread?.(),
            stopStreaming: () => slashCommandUiHooksRef.current.stopStreaming?.(),
            submitSideQuestion: (prompt: string) =>
              slashCommandUiHooksRef.current.submitSideQuestion?.(prompt),
          },
        }
      : null),
    [currentUser?.id, workspaceId, router, slashCommandSupabase, isAdmin]
  )

  const navigateFromChat = useCallback((path: string) => {
    if (onNavigate) {
      onNavigate(path)
      return
    }
    flagUrlChangeSource('tool-navigation')
    router.push(path)
  }, [flagUrlChangeSource, onNavigate, router])

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [threadModel, setThreadModel] = useState<string | null>(null)
  const [toolMode, setToolMode] = useState<ChatToolMode>('agent')
  const toolModeInitializedRef = useRef(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isHydrating, setIsHydrating] = useState(true)
  const [sessionId] = useState(() => `chat-${Date.now()}`)
  const [campaignId, setCampaignId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesScrollRef = useRef<HTMLDivElement>(null)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const showScrollToBottomRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const seedSentRef = useRef(false)
  const autoSendHandledRef = useRef(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Hydration tracking for scroll behavior
  const isHydratingRef = useRef(true)

  // Tool calls tracked in state
  const [toolCalls, setToolCalls] = useState<Map<string, ToolCallState>>(new Map())
  const toolCallIndexRef = useRef(0)

  // Internal thread state (may differ from externalThreadId during creation)
  const [internalThreadId, setInternalThreadId] = useState<string | null>(externalThreadId ?? null)

  // Thread UI state (single conversation — no thread management)
  const [threadTitle, setThreadTitle] = useState('Chat')
  const [isFirstAssistantResponse, setIsFirstAssistantResponse] = useState(true)

  // Action button hover state
  const [hoveredMsgIdx, setHoveredMsgIdx] = useState<number | null>(null)
  const [copiedMsgIdx, setCopiedMsgIdx] = useState<number | null>(null)
  const [feedbackIdx, setFeedbackIdx] = useState<number | null>(null)
  const [feedbackRating, setFeedbackRating] = useState<'positive' | 'negative' | null>(null)
  const [feedbackText, setFeedbackText] = useState('')
  const [submittingFeedback, setSubmittingFeedback] = useState(false)

  // File attachment state (previewUrl is a blob URL for images/videos — revoke on remove/unmount)
  const [pendingAttachments, setPendingAttachments] = useState<Array<{ id: string; file_name: string; file_type: string; file_size: number; previewUrl?: string }>>([])
  const pendingAttachmentsRef = useRef<Array<{ id: string; file_name: string; file_type: string; file_size: number; previewUrl?: string }>>([])
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const hasContent = input.trim().length > 0 || pendingAttachments.length > 0

  // Infinite scroll state
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [oldestCreatedAt, setOldestCreatedAt] = useState<string | null>(null)
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null)

  // Bookmarks state
  const [bookmarkedMessageIds, setBookmarkedMessageIds] = useState<Set<string>>(new Set())
  const [bookmarkPreviews, setBookmarkPreviews] = useState<Array<{ messageId: string; preview: string; created_at?: string }>>([])
  const [bookmarksOpen, setBookmarksOpen] = useState(false)
  const messageElementsRef = useRef<Map<string, HTMLElement>>(new Map())

  // Text selection quote state
  const [quotedTexts, setQuotedTexts] = useState<Array<{ text: string; messageIndex?: number }>>([])
  const quotedTextsRef = useRef<Array<{ text: string; messageIndex?: number }>>([])
  useEffect(() => { quotedTextsRef.current = quotedTexts }, [quotedTexts])
  const [quoteButton, setQuoteButton] = useState<{ text: string; x: number; y: number; messageIndex?: number } | null>(null)

  // Sound mute preference
  const [soundsMuted, setSoundsMuted] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('monarch.sounds_muted') === 'true'
  })
  useEffect(() => {
    if (toolModeInitializedRef.current || typeof window === 'undefined') return
    const storedMode = localStorage.getItem(CHAT_MODE_STORAGE_KEY)
    setToolMode(isChatToolMode(storedMode) ? storedMode : godMode ? 'ask' : 'agent')
    toolModeInitializedRef.current = true
  }, [godMode])
  const handleToolModeChange = useCallback((nextMode: ChatToolMode) => {
    setToolMode(nextMode)
    if (typeof window !== 'undefined') {
      localStorage.setItem(CHAT_MODE_STORAGE_KEY, nextMode)
    }
  }, [])
  // Ref to suppress duplicate receive sound within a single stream
  const receiveSoundFiredRef = useRef(false)

  // Stable ref to current thread ID for use in closures
  const threadIdRef = useRef<string | null>(internalThreadId)
  useEffect(() => { threadIdRef.current = internalThreadId }, [internalThreadId])

  /** Avoids workspace `getOrCreateThread` racing the first guest send (bootstrap + thread POST). */
  const deferWorkspaceThreadSyncRef = useRef(false)

  /** Prevents concurrent thread creation (mutex). */
  const threadCreatingRef = useRef(false)

  /** Silence timeout ref for 60s stream watchdog. */
  const streamTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * Drives the "still working..." pulse next to the typing dots when a
   * turn takes longer than ~15s with no text_delta. Set to true by a
   * separate timer in processSSE; cleared on each text_delta and on
   * stream completion. (Phase 5 of chat robustness roadmap.)
   */
  const [streamSlow, setStreamSlow] = useState(false)
  const streamSlowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Online/offline banner driven by window.navigator events. */
  const [isOffline, setIsOffline] = useState(
    typeof window !== 'undefined' ? !window.navigator.onLine : false,
  )
  const lastUserMessageRef = useRef<string>('')

  const updateScrollToBottomVisibility = useCallback(() => {
    const el = messagesScrollRef.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const next = distanceFromBottom > 80
    if (showScrollToBottomRef.current === next) return
    showScrollToBottomRef.current = next
    setShowScrollToBottom(next)
  }, [])

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (pendingScrollToMessageId) return
    const behavior = isHydratingRef.current ? 'auto' : 'smooth'
    messagesEndRef.current?.scrollIntoView({ behavior })
  }, [messages, pendingScrollToMessageId])

  // Scroll to bottom instantly when hydration completes
  useEffect(() => {
    if (!isHydrating) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
    }
  }, [isHydrating])

  // Scroll to bottom instantly when the panel becomes active/visible
  useEffect(() => {
    if (panelActive && !pendingScrollToMessageId) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
    }
  }, [panelActive, pendingScrollToMessageId])

  // Jump to a bookmarked message after opening the panel (e.g. from collapsed-rail popover)
  useLayoutEffect(() => {
    if (variant !== 'panel' || !panelActive || !pendingScrollToMessageId || isHydrating) return
    const id = pendingScrollToMessageId
    const attempt = (depth: number) => {
      const el = messageElementsRef.current.get(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setPendingScrollToMessageId(null)
        return
      }
      if (depth < 2) {
        requestAnimationFrame(() => { attempt(depth + 1) })
        return
      }
      setPendingScrollToMessageId(null)
      toast('Message may be in older history — scroll up to load more')
    }
    attempt(0)
  }, [variant, panelActive, pendingScrollToMessageId, isHydrating, messages, setPendingScrollToMessageId])

  useLayoutEffect(() => {
    updateScrollToBottomVisibility()
  }, [messages, isLoading, isHydrating, updateScrollToBottomVisibility])

  // Mark hydration complete after first messages load
  useEffect(() => {
    if (messages.length > 0 && isHydratingRef.current) {
      isHydratingRef.current = false
    }
  }, [messages.length])

  // Clear idle extraction timer on unmount
  useEffect(() => {
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    }
  }, [])

  // Revoke all blob preview URLs on unmount
  useEffect(() => {
    return () => {
      pendingAttachmentsRef.current.forEach(a => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      })
    }
  }, [])

  // Text selection → floating quote button
  useEffect(() => {
    const handleMouseUp = (e: MouseEvent) => {
      // Don't show if click is on the quote button itself
      if ((e.target as HTMLElement)?.closest('[data-quote-button]')) return

      const selection = window.getSelection()
      if (!selection || selection.isCollapsed || !selection.toString().trim()) {
        setQuoteButton(null)
        return
      }

      const range = selection.getRangeAt(0)
      const container = range.commonAncestorContainer
      const msgEl = container.nodeType === Node.TEXT_NODE
        ? (container as Text).parentElement?.closest('[data-assistant-message]')
        : (container as Element).closest('[data-assistant-message]')

      if (!msgEl) {
        setQuoteButton(null)
        return
      }

      const rect = range.getBoundingClientRect()
      const msgIndex = msgEl.getAttribute('data-msg-index')
      setQuoteButton({
        text: selection.toString().trim(),
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
        messageIndex: msgIndex ? parseInt(msgIndex, 10) : undefined,
      })
    }

    document.addEventListener('mouseup', handleMouseUp)
    return () => document.removeEventListener('mouseup', handleMouseUp)
  }, [])

  // Dismiss quote button when the messages area scrolls
  useEffect(() => {
    const scrollEl = messagesScrollRef.current
    if (!scrollEl) return
    const handleScroll = () => setQuoteButton(null)
    scrollEl.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', handleScroll)
  }, [])

  // ─── Thread management ──────────────────────────────────────────────────────

  // Mode marker that scopes thread/message/bookmark queries server-side. When
  // god mode is on, we fetch a totally separate conversation — admin-only —
  // and persist new messages there too.
  const chatMode: 'normal' | 'god' = godMode ? 'god' : 'normal'
  const modeQS = `mode=${chatMode}`
  const promptControls = (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex shrink-0 items-center gap-0.5 text-[13px] font-medium text-atrium-black/40 transition-colors hover:text-atrium-black/60">
            <span>{MODEL_OPTIONS.find(m => m.value === threadModel)?.label ?? 'Auto'}</span>
            <HugeiconsIcon icon={ArrowDown01Icon} className="size-3" />
          </button>
        </PopoverTrigger>
        {/* z-[200] beats the chat shell at z-[110] in CampaignChatPanel.
            Default PopoverContent z-50 portals to <body> but loses to the
            fixed chat shell, so the picker would render under the sidebar. */}
        <PopoverContent align="start" className="z-[9999] w-56 p-1">
          {MODEL_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => {
                if ((opt as { disabled?: boolean }).disabled) return
                const prevModel = threadModel
                setThreadModel(opt.value)
                if (internalThreadId) {
                  fetch(`/api/chat/threads/${internalThreadId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ model: opt.value }),
                  }).catch(() => {
                    toast.error('Failed to save model preference')
                    setThreadModel(prevModel)
                  })
                }
              }}
              className={cn(
                'w-full rounded-md px-2 py-1.5 text-left text-[13px] transition-colors',
                (opt as { disabled?: boolean }).disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-atrium-content',
                threadModel === opt.value || (!threadModel && opt.value === 'auto') ? 'font-medium text-atrium-black' : 'text-atrium-black/70'
              )}
            >
              <div className="flex items-center gap-1">
                {opt.label}
                {(opt as { comingSoon?: boolean }).comingSoon && <span className="text-[10px] text-atrium-black/40">Soon</span>}
              </div>
              {(opt as { description?: string }).description && (
                <div className="mt-0.5 text-[10px] font-normal text-atrium-black/40">{(opt as { description?: string }).description}</div>
              )}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <div className="ml-1 flex shrink-0 items-center rounded-full border border-atrium-border bg-atrium-background p-0.5">
        {CHAT_MODE_OPTIONS.map(opt => (
          <Tooltip key={opt.value} delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => handleToolModeChange(opt.value)}
                aria-label={opt.tooltip}
                aria-pressed={toolMode === opt.value}
                className={cn(
                  'flex h-6 items-center gap-1 rounded-full px-2 text-[12px] font-medium transition-colors',
                  toolMode === opt.value
                    ? 'bg-atrium-chatbar text-atrium-black'
                    : 'text-atrium-black/50 hover:bg-atrium-content hover:text-atrium-black/70'
                )}
              >
                <HugeiconsIcon icon={opt.icon} className="size-3.5" />
                <span className={mobileLayout ? 'sr-only' : undefined}>{opt.label}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-56 text-xs">
              {opt.tooltip}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </>
  )

  // Cap the inner message column at max-w-3xl so reading width matches
  // the fullscreen page no matter how wide the chat panel gets resized.
  // Applies in BOTH the regular sidebar panel (so wide drags don't make
  // text run edge-to-edge) AND viewport-fullscreen overlay. Outer message
  // rows stay full width so group-hover highlights still span the panel.
  // For narrow panels (< ~768px) this is a no-op since max-w-3xl > panel
  // width; the cap only kicks in once the panel is wide enough to need it.
  const narrowMessageColumn = variant === 'panel'
  const centeredChatColumn = variant !== 'panel' || panelViewportFullscreen
  const messageListColumnClass = centeredChatColumn ? CHAT_COLUMN_CLASS : 'mx-auto w-full max-w-none px-4'
  const composerColumnClass =
    variant === 'panel' && !panelViewportFullscreen ? CHAT_PANEL_COMPOSER_CLASS : CHAT_COLUMN_CLASS
  const shellBgClass = variant === 'panel' ? 'bg-atrium-sidebar' : 'bg-atrium-chatbar'
  const shellFadeFromClass = variant === 'panel' ? 'from-atrium-sidebar' : 'from-atrium-chatbar'
  const shellFadeToClass = variant === 'panel' ? 'to-atrium-sidebar/0' : 'to-atrium-chatbar/0'
  const threadsUrl = workspaceId
    ? `/api/chat/threads?workspaceId=${workspaceId}&${modeQS}`
    : `/api/chat/threads?${modeQS}`

  // Single conversation: get existing thread or create one
  const getOrCreateThread = useCallback(async () => {
    // Sherman Phase 1: in v2 mode, start fresh with no thread loaded.
    // The sidebar lets the user pick a thread; otherwise, the first send
    // creates a new one. Skip the auto-pick-most-recent behavior that
    // makes the chat feel like one infinite conversation.
    if (chatThreadsV2 && !externalThreadId) {
      return
    }
    try {
      // Try to find existing thread
      const threadsRes = await fetch(threadsUrl)
      const threadsData = await threadsRes.json()
      const existingThreads = (threadsData.threads || []) as ThreadItem[]

      if (existingThreads.length > 0) {
        // Use the most recent thread — this is the canonical conversation
        const thread = existingThreads[0]
        setInternalThreadId(thread.id)
        threadIdRef.current = thread.id
        setThreadTitle(thread.title || 'Chat')
        setThreadModel(thread.model ?? null)
        onThreadCreated?.(thread.id, thread.title)

        // Hydrate messages — load from ALL threads in the workspace for unified view.
        // (Server-side this is two RPCs today; we have a separate spec to denormalize
        // workspace_id/user_id/mode onto chat_messages and collapse to a single index
        // scan. Once that lands, this stays workspace-wide and gets faster.)
        setIsHydrating(true)
        try {
          // Sherman Phase 1: when v2 is on, ALWAYS hydrate from the
          // selected thread only. The workspaceId-wide endpoint is the
          // legacy "unified view" that produced the infinite-scroll feel.
          const messagesEndpoint = chatThreadsV2
            ? `/api/chat/threads/${thread.id}/messages?limit=50`
            : workspaceId
              ? `/api/chat/messages?workspaceId=${workspaceId}&limit=50&${modeQS}`
              : `/api/chat/threads/${thread.id}/messages?limit=50`
          const res = await fetch(messagesEndpoint)
          if (res.ok) {
            const data = await res.json()
            if (data.messages && data.messages.length > 0) {
              // Filter out system tool receipt messages — they're displayed inline, not as separate messages
              const filtered = data.messages.filter((m: { role: string; content: string; metadata?: { kickoff?: boolean } | null }) =>
                // Filter out system tool receipt messages (legacy emoji-prefixed and [tool_receipt] tagged)
                !(m.role === 'system' && ((m.content ?? '').startsWith('[tool_receipt]') || /^[\u{1F300}-\u{1FAD6}]/u.test(m.content ?? '')))
                && !(m.role === 'user' && m.metadata?.kickoff === true)
              )
              // Drop back-to-back duplicate user rows (e.g. seed auto-send + client persist race).
              const deduped = filtered.filter((m: { role: string; content: string }, i: number, arr: typeof filtered) => {
                if (i === 0) return true
                const prev = arr[i - 1]
                return !(m.role === 'user' && prev.role === 'user' && (m.content ?? '').trim() === (prev.content ?? '').trim())
              })

              // Reconstruct tool calls from persisted metadata
              const restoredToolCalls = new Map<string, ToolCallState>()
              let toolIdx = 0

              const hydratedMessages = deduped.map((m: { id?: string; thread_id?: string; role: string; content: string; channel?: string; created_at?: string; metadata?: { toolCalls?: Array<{ name: string; input: Record<string, unknown>; result?: unknown; status: string }>; segments?: Array<{ type: 'text'; content: string } | { type: 'tool'; name: string; input: Record<string, unknown>; result?: unknown; status: string }>; attachments?: Array<{ id: string; file_name: string; file_type: string; file_size: number }>; interrupted?: boolean } }, mIdx: number, mArr: Array<typeof m>) => {
                // Detect persisted session dividers
                if (m.role === 'system' && m.content === '---new-session---') {
                  return { id: m.id, role: 'system' as const, content: '', isDivider: true, created_at: m.created_at }
                }

                const msg: ChatMessage = {
                  id: m.id,
                  role: m.role as 'user' | 'assistant' | 'system',
                  content: m.content,
                  channel: m.channel,
                  created_at: m.created_at,
                  ...(m.role === 'user' && m.metadata?.attachments?.length ? { attachments: m.metadata.attachments } : {}),
                }

                // Restore the "interrupted, retry?" banner on hydration. The
                // server stamps metadata.interrupted = true on assistant
                // rows whose stream errored or whose client aborted. We
                // find the immediately-preceding user message to seed the
                // retry button. (Spec: chat-audit pass 2, B16.)
                if (m.role === 'assistant' && m.metadata?.interrupted) {
                  const prevUser = (() => {
                    for (let i = mIdx - 1; i >= 0; i--) {
                      const p = mArr[i]
                      if (p?.role === 'user') return p
                    }
                    return null
                  })()
                  if (prevUser?.content) {
                    msg.isRetryable = true
                    msg.retryMessage = prevUser.content
                  }
                }

                // If this assistant message has tool calls in metadata, rebuild segments
                if (m.role === 'assistant' && m.metadata?.toolCalls?.length) {
                  const segments: MessageSegment[] = []

                  if (m.metadata.segments?.length) {
                    // New format: use persisted ordered segments for faithful interleaved rendering
                    let textContent = ''
                    for (const seg of m.metadata.segments) {
                      if (seg.type === 'text') {
                        const content = seg.content.trim()
                        if (content && content !== '(tool calls only)') {
                          segments.push({ type: 'text', content })
                          if (textContent) textContent += '\n\n'
                          textContent += content
                        }
                      } else if (seg.type === 'tool') {
                        // Handle show_options specially — extract options, don't render as tool card
                        if (seg.name === 'show_options' && seg.result && typeof seg.result === 'object') {
                          const opts = (seg.result as Record<string, unknown>).options as string[] | undefined
                          if (opts?.length) msg.options = opts
                          continue
                        }
                        const key = `restored-${toolIdx++}`
                        restoredToolCalls.set(key, {
                          name: seg.name,
                          input: seg.input || {},
                          result: seg.result,
                          status: (seg.status === 'error' ? 'error' : 'complete') as 'complete' | 'error',
                        })
                        segments.push({ type: 'tool', toolKey: key })
                      }
                    }
                    msg.content = textContent
                  } else {
                    // Legacy fallback: old messages without ordered segments — text first, then tool cards
                    let textContent = m.content
                      .split('\n\n')
                      .filter(line => !line.startsWith('[tool_receipt]') && !/^[\u{1F300}-\u{1FAD6}]/u.test(line))
                      .join('\n\n')
                      .trim()

                    if (textContent === '(tool calls only)') textContent = ''

                    const textParagraphs = textContent ? textContent.split('\n\n').filter(Boolean) : []
                    const toolCalls = m.metadata.toolCalls.filter(tc => {
                      if (tc.name === 'show_options' && tc.result && typeof tc.result === 'object') {
                        const opts = (tc.result as Record<string, unknown>).options as string[] | undefined
                        if (opts?.length) msg.options = opts
                        return false
                      }
                      return true
                    })

                    if (textParagraphs.length > 0) {
                      segments.push({ type: 'text', content: textParagraphs.join('\n\n') })
                    }

                    for (const tc of toolCalls) {
                      const key = `restored-${toolIdx++}`
                      restoredToolCalls.set(key, {
                        name: tc.name,
                        input: tc.input || {},
                        result: tc.result,
                        status: (tc.status === 'error' ? 'error' : 'complete') as 'complete' | 'error',
                      })
                      segments.push({ type: 'tool', toolKey: key })
                    }

                    msg.content = textContent
                  }

                  msg.segments = segments.length > 0 ? segments : undefined
                }

                return msg
              })

              setToolCalls(prev => {
                const merged = new Map(prev)
                restoredToolCalls.forEach((v, k) => merged.set(k, v))
                return merged
              })
              // Insert session dividers where thread_id changes between consecutive messages.
              // Sherman Phase 1 (v2): each thread renders alone — skip the
              // divider weaving entirely.
              let withDividers: ChatMessage[]
              if (chatThreadsV2) {
                withDividers = hydratedMessages.filter((m: ChatMessage | undefined): m is ChatMessage => !!m)
              } else {
                withDividers = []
                let prevThreadId: string | undefined
                for (let idx = 0; idx < deduped.length; idx++) {
                  const raw = deduped[idx] as { thread_id?: string }
                  if (raw.thread_id && prevThreadId && raw.thread_id !== prevThreadId) {
                    withDividers.push({ role: 'system', content: '', isDivider: true })
                  }
                  const hydrated = hydratedMessages[idx]
                  if (hydrated) withDividers.push(hydrated)
                  if (raw.thread_id) prevThreadId = raw.thread_id
                }
              }

              setMessages(withDividers)
              setIsFirstAssistantResponse(false)

              // If we loaded the max batch, there may be older messages available
              if (withDividers.length >= 50) {
                setHasMore(true)
                const oldest = withDividers.find((m: ChatMessage) => m.created_at)
                setOldestCreatedAt(oldest?.created_at ?? null)
              } else {
                setHasMore(false)
              }
            }
          }
        } finally {
          setIsHydrating(false)
        }
        return
      }

      // No existing thread — create one in the active mode
      const res = await fetch(threadsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flow_intent: flowIntent, workspaceId, mode: chatMode }),
      })
      const data = await res.json()
      if (data.thread) {
        setInternalThreadId(data.thread.id)
        threadIdRef.current = data.thread.id
        setThreadTitle('Chat')
        setIsFirstAssistantResponse(true)
        setMessages([])
        setToolCalls(new Map())
        onThreadCreated?.(data.thread.id, data.thread.title)

        if (!seedMessage) {
          setMessages([])
        }
      }
      setIsHydrating(false)
    } catch {
      setMessages([])
      setIsHydrating(false)
    }
  }, [flowIntent, seedMessage, onThreadCreated, chatMode, threadsUrl, workspaceId, modeQS]) // eslint-disable-line react-hooks/exhaustive-deps

  const prevWorkspaceIdRef = useRef<string | null | undefined>(undefined)
  const prevModeRef = useRef<'normal' | 'god' | undefined>(undefined)

  const initialLoadDoneRef = useRef(false)

  // Restore or create the single conversation when workspace, mode, or panel
  // readiness changes. A mode flip is treated identically to a workspace
  // switch: abort stream, blank in-memory state, then load the conversation
  // for the new (workspace, mode) pair.
  useEffect(() => {
    if (!workspaceReady) return
    if (deferWorkspaceThreadSyncRef.current) return
    // Logged-out (or no workspace yet): allow composer; first send bootstraps session + thread.
    if (!workspaceId) {
      setIsHydrating(false)
      prevWorkspaceIdRef.current = null
      prevModeRef.current = chatMode
      return
    }

    const prevWs = prevWorkspaceIdRef.current
    const prevMode = prevModeRef.current
    prevWorkspaceIdRef.current = workspaceId
    prevModeRef.current = chatMode
    const wsChanged =
      typeof prevWs === 'string' && typeof workspaceId === 'string' && prevWs !== workspaceId
    const modeChanged = typeof prevMode === 'string' && prevMode !== chatMode

    // Skip re-fetching on tab focus / re-renders when neither workspace nor mode actually changed
    if (initialLoadDoneRef.current && !wsChanged && !modeChanged) return

    if (wsChanged || modeChanged) {
      // Abort any active stream before switching context
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
        setIsLoading(false)
      }
      setMessages([])
      setToolCalls(new Map())
      setInternalThreadId(null)
      threadIdRef.current = null
      seedSentRef.current = false
      autoSendHandledRef.current = false
      toolCallIndexRef.current = 0
    }

    if (externalThreadId && !modeChanged) {
      setInternalThreadId(externalThreadId)
      threadIdRef.current = externalThreadId
    }
    initialLoadDoneRef.current = true
    getOrCreateThread()
  }, [workspaceId, workspaceReady, chatMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Infinite scroll — load older messages ──────────────────────────────────

  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMore || !hasMore || !oldestCreatedAt) return
    setIsLoadingMore(true)
    try {
      // Sherman Phase 1: when v2 is on, scroll-back paginates the
      // current thread only. Pre-v2 behavior pages across the workspace.
      const params = new URLSearchParams({
        ...(chatThreadsV2
          ? (internalThreadId ? { threadId: internalThreadId } : {})
          : workspaceId
            ? { workspaceId, mode: chatMode }
            : internalThreadId
              ? { threadId: internalThreadId }
              : {}),
        before: oldestCreatedAt,
        limit: '30',
      })
      const res = await fetch(`/api/chat/messages?${params}`)
      if (!res.ok) return
      const data = await res.json()
      const { messages: rawOlder, hasMore: moreAvailable } = data as {
        messages: Array<{ id?: string; thread_id?: string; role: string; content: string; channel?: string; created_at?: string; metadata?: { toolCalls?: Array<{ name: string; input: Record<string, unknown>; result?: unknown; status: string }>; segments?: Array<{ type: 'text'; content: string } | { type: 'tool'; name: string; input: Record<string, unknown>; result?: unknown; status: string }>; attachments?: Array<{ id: string; file_name: string; file_type: string; file_size: number }>; interrupted?: boolean; kickoff?: boolean } }>
        hasMore: boolean
      }

      if (!rawOlder || rawOlder.length === 0) {
        setHasMore(false)
        return
      }

      // Filter system tool receipt messages (same logic as hydration)
      const filtered = rawOlder.filter(m =>
        !(m.role === 'system' && ((m.content ?? '').startsWith('[tool_receipt]') || /^[\u{1F300}-\u{1FAD6}]/u.test(m.content ?? '')))
        && !(m.role === 'user' && m.metadata?.kickoff === true)
      )

      const restoredToolCalls = new Map<string, ToolCallState>()
      let toolIdx = toolCallIndexRef.current

      const olderMessages: ChatMessage[] = filtered.map((m, mIdx, mArr) => {
        // Detect persisted session dividers
        if (m.role === 'system' && m.content === '---new-session---') {
          return { id: m.id, role: 'system' as const, content: '', isDivider: true, created_at: m.created_at }
        }

        const msg: ChatMessage = {
          id: m.id,
          role: m.role as 'user' | 'assistant' | 'system',
          content: m.content,
          channel: m.channel,
          created_at: m.created_at,
          ...(m.role === 'user' && m.metadata?.attachments?.length ? { attachments: m.metadata.attachments } : {}),
        }

        // Restore interrupted-banner state when paginating into older
        // messages (same logic as the initial-load hydration above).
        // (Spec: chat-audit pass 2, B16.)
        if (m.role === 'assistant' && m.metadata?.interrupted) {
          const prevUser = (() => {
            for (let i = mIdx - 1; i >= 0; i--) {
              const p = mArr[i]
              if (p?.role === 'user') return p
            }
            return null
          })()
          if (prevUser?.content) {
            msg.isRetryable = true
            msg.retryMessage = prevUser.content
          }
        }

        if (m.role === 'assistant' && m.metadata?.toolCalls?.length) {
          const segments: MessageSegment[] = []

          if (m.metadata.segments?.length) {
            // New format: use persisted ordered segments
            let textContent = ''
            for (const seg of m.metadata.segments) {
              if (seg.type === 'text') {
                const content = seg.content.trim()
                if (content && content !== '(tool calls only)') {
                  segments.push({ type: 'text', content })
                  if (textContent) textContent += '\n\n'
                  textContent += content
                }
              } else if (seg.type === 'tool') {
                if (seg.name === 'show_options' && seg.result && typeof seg.result === 'object') {
                  const opts = (seg.result as Record<string, unknown>).options as string[] | undefined
                  if (opts?.length) msg.options = opts
                  continue
                }
                const key = `restored-${toolIdx++}`
                restoredToolCalls.set(key, {
                  name: seg.name,
                  input: seg.input || {},
                  result: seg.result,
                  status: (seg.status === 'error' ? 'error' : 'complete') as 'complete' | 'error',
                })
                segments.push({ type: 'tool', toolKey: key })
              }
            }
            msg.content = textContent
          } else {
            // Legacy fallback
            let textContent = m.content
              .split('\n\n')
              .filter(line => !line.startsWith('[tool_receipt]') && !/^[\u{1F300}-\u{1FAD6}]/u.test(line))
              .join('\n\n')
              .trim()
            if (textContent === '(tool calls only)') textContent = ''

            const textParagraphs = textContent ? textContent.split('\n\n').filter(Boolean) : []
            const tcs = m.metadata.toolCalls.filter(tc => {
              if (tc.name === 'show_options' && tc.result && typeof tc.result === 'object') {
                const opts = (tc.result as Record<string, unknown>).options as string[] | undefined
                if (opts?.length) msg.options = opts
                return false
              }
              return true
            })

            if (textParagraphs.length > 0) {
              segments.push({ type: 'text', content: textParagraphs.join('\n\n') })
            }
            for (const tc of tcs) {
              const key = `restored-${toolIdx++}`
              restoredToolCalls.set(key, {
                name: tc.name,
                input: tc.input || {},
                result: tc.result,
                status: (tc.status === 'error' ? 'error' : 'complete') as 'complete' | 'error',
              })
              segments.push({ type: 'tool', toolKey: key })
            }
            msg.content = textContent
          }
          msg.segments = segments.length > 0 ? segments : undefined
        }

        return msg
      })

      toolCallIndexRef.current = toolIdx

      // Insert session dividers where thread_id changes between consecutive messages.
      // Sherman Phase 1 (v2): each thread renders alone — skip the
      // divider weaving entirely.
      let withDividers: ChatMessage[]
      if (chatThreadsV2) {
        withDividers = olderMessages
      } else {
        withDividers = []
        let lastThreadId: string | undefined
        for (const [idx, m] of filtered.entries()) {
          if (m.thread_id && lastThreadId && m.thread_id !== lastThreadId) {
            withDividers.push({ role: 'system', content: '', isDivider: true })
          }
          withDividers.push(olderMessages[idx])
          if (m.thread_id) lastThreadId = m.thread_id
        }
      }

      // Also check if the first existing message has a different thread than the last loaded one
      // (to add a divider at the boundary between loaded and existing messages)

      // Maintain scroll position: save scrollHeight before prepending, restore after
      const scrollEl = messagesScrollRef.current
      const prevScrollHeight = scrollEl?.scrollHeight ?? 0

      setToolCalls(prev => {
        const merged = new Map(prev)
        restoredToolCalls.forEach((v, k) => merged.set(k, v))
        return merged
      })
      setMessages(prev => [...withDividers, ...prev])
      setHasMore(moreAvailable)
      if (olderMessages.length > 0) {
        const oldest = olderMessages.find(m => m.created_at)
        setOldestCreatedAt(oldest?.created_at ?? oldestCreatedAt)
      }

      // Restore scroll so visible content doesn't jump
      requestAnimationFrame(() => {
        if (scrollEl) {
          const newScrollHeight = scrollEl.scrollHeight
          scrollEl.scrollTop = newScrollHeight - prevScrollHeight
        }
      })
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, hasMore, internalThreadId, workspaceId, oldestCreatedAt]) // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver: when the sentinel at the top of the list becomes visible, load more
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current
    if (!sentinel || !hasMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          void loadMoreMessages()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadMoreMessages])

  // Prefill input with seed message when chat opens with intent
  const lastSeedRef = useRef<string | null>(null)
  useEffect(() => {
    if (seedMessage && seedMessage !== lastSeedRef.current) {
      lastSeedRef.current = seedMessage
      if (autoSendSeed) return
      setInput(seedMessage)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [autoSendSeed, seedMessage]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Thread helpers ─────────────────────────────────────────────────────────

  // Assistant messages are persisted server-side in the API route.
  // Client no longer duplicates persistence to avoid race conditions.
  const persistAIMessage = (_threadId: string, _aiMsg: string) => {
    // no-op — server handles this
  }

  const autoTitleThread = (threadId: string, firstUserMessage: string) => {
    // Optimistic local title from the user message — displays
    // immediately while the LLM generates a better one.
    const truncated = firstUserMessage.length > 40
      ? firstUserMessage.substring(0, 40) + '...'
      : firstUserMessage
    setThreadTitle(truncated)

    if (chatThreadsV2) {
      // Sherman Phase 1: prefer LLM-generated titles via the auto-title
      // endpoint. Fire-and-forget; we refetch the thread to pick up the
      // generated title and update local state.
      fetch(`/api/chat/threads/${threadId}/auto-title`, { method: 'POST' })
        .then(async (res) => {
          if (!res.ok) return
          const data = await res.json().catch(() => null) as { generated?: boolean; title?: string } | null
          if (data?.generated && data.title) {
            setThreadTitle(data.title)
          }
        })
        .catch(() => {
          // Best-effort — the optimistic truncated title is already shown.
          // Persist it so the sidebar isn't blank on refresh.
          fetch(`/api/chat/threads/${threadId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: truncated }),
          }).catch(() => {})
        })
      return
    }

    // Legacy: persist the truncated title directly.
    fetch(`/api/chat/threads/${threadId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: truncated }),
    }).catch(() => {})
  }

  const startNewSession = useCallback(async () => {
    // Archive the current thread (mark it so it's not picked up by getOrCreateThread)
    if (threadIdRef.current) {
      await fetch(`/api/chat/threads/${threadIdRef.current}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived: true }),
      }).catch(() => {})
    }

    // Persist session divider to the DB so it survives refresh
    if (threadIdRef.current) {
      await fetch(`/api/chat/threads/${threadIdRef.current}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: 'system', content: '---new-session---' }),
      }).catch(() => {})
    }

    // Insert a session divider + spacer — old messages stay above, new session starts at bottom
    setMessages(prev => [
      ...prev,
      { role: 'system', content: '', isDivider: true },
      { role: 'system', content: '__spacer__' },
    ])

    // Scroll to bottom after new session divider is added
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 50)

    setToolCalls(new Map())
    setInternalThreadId(null)
    threadIdRef.current = null
    setIsFirstAssistantResponse(true)
    seedSentRef.current = false
    toolCallIndexRef.current = 0
    setPendingAttachments([])
    pendingAttachmentsRef.current = []
    autoSendHandledRef.current = false
    setHasMore(false)
    setOldestCreatedAt(null)
    setBookmarkedMessageIds(new Set())
    setBookmarkPreviews([])
    setQuotedTexts([])

    // Create fresh thread (in the active mode)
    try {
      const res = await fetch(threadsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flow_intent: flowIntent, workspaceId, mode: chatMode }),
      })
      const data = await res.json()
      if (data.thread) {
        setInternalThreadId(data.thread.id)
        threadIdRef.current = data.thread.id
        setThreadTitle('Chat')
        onThreadCreated?.(data.thread.id, data.thread.title)
      }
    } catch {
      toast.error('Failed to save conversation. Your messages may not be saved.')
    }
  }, [flowIntent, onThreadCreated, chatMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Bookmarks ──────────────────────────────────────────────────────────────

  // Load bookmarks for the entire workspace (unified thread model), scoped
  // to the current chat mode so admin bookmarks don't leak into the customer
  // sidebar (and vice-versa).
  useEffect(() => {
    const bookmarkParam = workspaceId
      ? `workspaceId=${workspaceId}&mode=${chatMode}`
      : internalThreadId
        ? `threadId=${internalThreadId}`
        : null
    if (!bookmarkParam) return
    fetch(`/api/chat/bookmarks?${bookmarkParam}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.bookmarks) return
        setBookmarkedMessageIds(new Set(data.bookmarks.map((b: { message_id: string }) => b.message_id)))
        setBookmarkPreviews(data.bookmarks.map((b: { message_id: string; message_preview: string; message_created_at?: string }) => ({
          messageId: b.message_id,
          preview: b.message_preview,
          created_at: b.message_created_at,
        })))
      })
      .catch(() => {})
  }, [workspaceId, internalThreadId, chatMode])

  const toggleBookmark = useCallback(async (msgId: string) => {
    if (!internalThreadId) return
    const wasBookmarked = bookmarkedMessageIds.has(msgId)

    // Optimistic update — both the ID set and the preview list
    setBookmarkedMessageIds(prev => {
      const next = new Set(prev)
      if (wasBookmarked) next.delete(msgId)
      else next.add(msgId)
      return next
    })

    if (!wasBookmarked) {
      // Add to preview list locally
      const msg = messages.find(m => m.id === msgId)
      if (msg) {
        const thinkingSummary = msg.thinkingText ? `[Thinking: ${msg.thinkingText.slice(0, 80)}…] ` : ''
        setBookmarkPreviews(prev => [{
          messageId: msgId,
          preview: (thinkingSummary + (msg.content?.slice(0, 150) || '')).slice(0, 150),
          created_at: msg.created_at,
        }, ...prev])
      }
    } else {
      // Remove from preview list locally
      setBookmarkPreviews(prev => prev.filter(b => b.messageId !== msgId))
    }

    // Find the message's actual thread_id (may differ from current thread in unified view)
    // For now, use internalThreadId as the bookmark API needs a thread_id
    try {
      const res = await fetch('/api/chat/bookmarks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_id: msgId, thread_id: internalThreadId }),
      })

      if (!res.ok) throw new Error('Bookmark toggle failed')
      // No re-fetch — trust the optimistic update
    } catch {
      // Revert optimistic update on error
      setBookmarkedMessageIds(prev => {
        const next = new Set(prev)
        if (wasBookmarked) next.add(msgId)
        else next.delete(msgId)
        return next
      })
      if (!wasBookmarked) {
        setBookmarkPreviews(prev => prev.filter(b => b.messageId !== msgId))
      }
    }
  }, [internalThreadId, bookmarkedMessageIds, messages])

  // Thread title still auto-saved for search indexing (even though not shown as editable)
  const handleSaveTitle = () => {
    // no-op in single conversation mode
  }

  // ─── File upload handler ──────────────────────────────────────────────────

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Reset so the same file can be re-selected
    e.target.value = ''

    if (!workspaceId) {
      toast.error('No workspace selected')
      return
    }

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('workspaceId', workspaceId)

      const response = await fetch('/api/chat/upload', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const data = await response.json()
        toast.error(data.error || 'Upload failed')
        return
      }

      const att = await response.json() as {
        id: string
        file_name: string
        file_type: string
        file_size: number
        metadata?: {
          domains?: string[]
          bundle_ids?: string[]
          app_names?: string[]
          parse_errors?: number
          is_sonobi_inventory?: boolean
        } | null
      }

      // Show feedback for CSV/TXT supply list files (domains, bundle IDs, app IDs)
      if (att.metadata?.domains !== undefined || att.metadata?.bundle_ids !== undefined) {
        const domainCount = att.metadata?.domains?.length ?? 0
        const bundleCount = att.metadata?.bundle_ids?.length ?? 0
        const totalEntries = domainCount + bundleCount

        if (totalEntries === 0) {
          toast.error('No valid domains or app IDs found in file.')
          return
        }

        const errorCount = att.metadata?.parse_errors ?? 0
        const errorNote = errorCount > 0 ? ` (${errorCount} invalid entries skipped)` : ''

        const parts: string[] = []
        if (domainCount > 0) parts.push(`${domainCount} domain${domainCount !== 1 ? 's' : ''}`)
        if (bundleCount > 0) parts.push(`${bundleCount} app ID${bundleCount !== 1 ? 's' : ''}`)

        toast.success(`${parts.join(' + ')} ready${errorNote}`)
      }

      // Create a local blob URL for image/video preview (revoked on remove or unmount)
      let previewUrl: string | undefined
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        previewUrl = URL.createObjectURL(file)
      }

      const attachment = { id: att.id, file_name: att.file_name, file_type: att.file_type, file_size: att.file_size, previewUrl }
      setPendingAttachments([attachment])
      pendingAttachmentsRef.current = [attachment]
      setTimeout(() => inputRef.current?.focus(), 50)
    } catch {
      toast.error('Upload failed')
    } finally {
      setIsUploading(false)
    }
  }, [workspaceId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handle dropped file from global overlay ───────────────────────────────

  const handleRawFile = useCallback(async (file: File) => {
    if (!workspaceId) {
      toast.error('No workspace selected')
      return
    }

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('workspaceId', workspaceId)

      const response = await fetch('/api/chat/upload', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const data = await response.json()
        toast.error(data.error || 'Upload failed')
        return
      }

      const att = await response.json() as {
        id: string
        file_name: string
        file_type: string
        file_size: number
        metadata?: {
          domains?: string[]
          bundle_ids?: string[]
          parse_errors?: number
        } | null
      }

      if (att.metadata?.domains !== undefined || att.metadata?.bundle_ids !== undefined) {
        const domainCount = att.metadata?.domains?.length ?? 0
        const bundleCount = att.metadata?.bundle_ids?.length ?? 0
        const totalEntries = domainCount + bundleCount
        if (totalEntries > 0) {
          const errorCount = att.metadata?.parse_errors ?? 0
          const errorNote = errorCount > 0 ? ` (${errorCount} skipped)` : ''
          const parts: string[] = []
          if (domainCount > 0) parts.push(`${domainCount} domain${domainCount !== 1 ? 's' : ''}`)
          if (bundleCount > 0) parts.push(`${bundleCount} app ID${bundleCount !== 1 ? 's' : ''}`)
          toast.success(`${parts.join(' + ')} ready${errorNote}`)
        }
      }

      let previewUrl: string | undefined
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        previewUrl = URL.createObjectURL(file)
      }

      const attachment = { id: att.id, file_name: att.file_name, file_type: att.file_type, file_size: att.file_size, previewUrl }
      setPendingAttachments([attachment])
      pendingAttachmentsRef.current = [attachment]
      setTimeout(() => inputRef.current?.focus(), 50)
    } catch {
      toast.error('Upload failed')
    } finally {
      setIsUploading(false)
    }
  }, [workspaceId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Consume dropped file from global overlay
  useEffect(() => {
    if (droppedFile && panelActive) {
      handleRawFile(droppedFile)
      setDroppedFile(null)
    }
  }, [droppedFile, panelActive, handleRawFile, setDroppedFile])

  // ─── Memory extraction on tab close (Fix 12) ─────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (extractEndpoint && internalThreadId) {
        navigator.sendBeacon(extractEndpoint.replace('[threadId]', internalThreadId))
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [extractEndpoint, internalThreadId])

  // ─── Online/offline banner (Phase 5 robustness) ───────────────────
  // When the user's connection drops mid-stream the SSE reader stalls;
  // pre-this-PR we surfaced that as a generic "response timed out" after
  // 120s. Now we detect it earlier from the browser's own online state
  // and render a banner so the user knows what happened.
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // ─── Export chat (Fix 15) ─────────────────────────────────────────────────

  const exportChat = useCallback(() => {
    const exportable = messages.filter(m => m.role === 'user' || m.role === 'assistant')
    if (exportable.length === 0) return
    const md = exportable.map(m => `## ${m.role === 'user' ? 'You' : 'AI'}\n\n${m.content}\n`).join('\n---\n\n')
    const blob = new Blob([md], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'chat-export.md'
    a.click()
    URL.revokeObjectURL(url)
  }, [messages])

  // ─── Send message ────────────────────────────────────────────────────────────

  const sendMessage = useCallback(async (userMessage: string, priorMessages?: ChatMessage[]) => {
    if (isLoading) return

    const pendingAtts = pendingAttachmentsRef.current

    // If no text but have pending attachments, auto-generate the user message
    let effectiveMessage = userMessage
    if (!effectiveMessage.trim() && pendingAtts.length > 0) {
      effectiveMessage = `I uploaded ${pendingAtts.map(a => a.file_name).join(', ')}`
    }

    if (!effectiveMessage.trim()) return

    const activeWorkspaceId = workspaceId ?? null
    if (!activeWorkspaceId) {
      toast.error('Sign in to start chatting')
      return
    }
    try {
      if (!threadIdRef.current) {
        if (threadCreatingRef.current) return
        threadCreatingRef.current = true
        try {
          const res = await fetch('/api/chat/threads', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ flow_intent: flowIntent ?? null, workspaceId: activeWorkspaceId, mode: chatMode }),
          })
          const data = await res.json()
          if (!data.thread?.id) {
            toast.error(data.error || 'Could not start chat thread')
            return
          }
          setInternalThreadId(data.thread.id)
          threadIdRef.current = data.thread.id
          onThreadCreated?.(data.thread.id, data.thread.title)
        } finally {
          threadCreatingRef.current = false
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not start chat'
      toast.error(msg)
      return
    }

    isHydratingRef.current = false

    // Resolve option shortcuts: "A", "B", "C" etc. → the actual option text
    let resolvedMessage = effectiveMessage
    // Capture quoted texts as metadata — shown above the bubble, prepended to API context
    const currentQuotes = quotedTextsRef.current
    if (currentQuotes.length > 0) {
      setQuotedTexts([])
      quotedTextsRef.current = []
    }
    const baseMessages = priorMessages ?? messages
    const lastMsg = baseMessages[baseMessages.length - 1]
    if (lastMsg?.options?.length) {
      const trimmed = userMessage.trim().toUpperCase()
      if (trimmed.length === 1 && trimmed >= 'A' && trimmed <= 'J') {
        const idx = trimmed.charCodeAt(0) - 65
        if (idx < lastMsg.options.length) {
          resolvedMessage = lastMsg.options[idx]
        }
      }
    }

    // Prepend quote context to the API message so the AI has the full context,
    // but keep the displayed message clean (quotes shown as a styled block above the bubble)
    const quotePrefix = currentQuotes.length > 0
      ? `${currentQuotes.map(q => `> ${q.text}`).join('\n\n')}\n\n`
      : ''

    // Append video intent detection note for video attachments
    const videoAtts = pendingAtts.filter(a => (a.file_type ?? '').startsWith('video/'))
    const videoNote = videoAtts.length > 0
      ? `\n\n[User uploaded a video file: ${videoAtts.map(a => a.file_name).join(', ')}. Determine if this is: (a) a brand asset to save to the brand library, (b) their own ad creative to attach to a campaign, or (c) a reference video for context. Ask the user to clarify if ambiguous, then route accordingly.]`
      : ''

    const apiMessage = apiMessageTransform
      ? apiMessageTransform(quotePrefix + resolvedMessage + videoNote)
      : quotePrefix + resolvedMessage + videoNote

    const pendingAttsForMessage = pendingAttachmentsRef.current.length > 0
      ? pendingAttachmentsRef.current.map(a => ({ id: a.id, file_name: a.file_name, file_type: a.file_type, file_size: a.file_size }))
      : undefined

    const newMessages: ChatMessage[] = [
      ...baseMessages,
      {
        role: 'user',
        content: resolvedMessage,
        ...(currentQuotes.length > 0 ? { quotedTexts: currentQuotes } : {}),
        ...(pendingAttsForMessage ? { attachments: pendingAttsForMessage } : {}),
      },
    ]
    setMessages([...newMessages, { role: 'assistant', content: '', isTyping: true }])
    setInput('')
    setIsLoading(true)
    receiveSoundFiredRef.current = false
    lastUserMessageRef.current = resolvedMessage
    playSendSound()

    // Persist user message FIRST, before calling AI.
    //
    // We pre-allocate a stable UUID for the user message so the client's
    // fire-and-forget persist below AND the server's authoritative persist
    // (in /api/campaign-chat) both upsert against the SAME id. Without
    // that coordination the two writers could race and produce duplicate
    // user message rows in chat_messages.
    // (Spec: chat-audit pass 2, B15.)
    const userMessageId = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`

    const currentThreadId = threadIdRef.current
    if (currentThreadId) {
      const persistBody: Record<string, unknown> = {
        id: userMessageId,
        role: 'user',
        content: effectiveMessage,
      }
      if (pendingAttsForMessage?.length) {
        persistBody.metadata = { attachments: pendingAttsForMessage }
      }
      void fetch(`/api/chat/threads/${currentThreadId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(persistBody),
      })
        .then((res) => {
          if (res.ok) requestUserChatActivityRefresh()
        })
        .catch(() => {})
    }

    let streamingContent = ''

    try {
      const apiMessages = newMessages
        .filter((m) => m.role !== 'tool')
        .map((m, i, arr) => {
          const content = i === arr.length - 1
            ? apiMessage
            : (m.content || '')
                .replace(/<function_calls>[\s\S]*?<\/function_calls>/g, '')
                .replace(/<tool_call>[\s\S]*?<\/tool_call>/g, '')
                .replace(/<invoke[\s\S]*?<\/invoke>/g, '')
                .replace(/<campaign_brief>[\s\S]*?<\/campaign_brief>/g, '')
                .replace(/<create_campaign\s*\/>/g, '')
                .replace(/<attach_creative>[\s\S]*?<\/attach_creative>/g, '')
                .trim()
          return { role: m.role, content }
        })
        .filter((m) => m.content.length > 0)

      const controller = new AbortController()
      abortControllerRef.current = controller

      const currentAttachments = pendingAttachmentsRef.current
      setPendingAttachments([])
      pendingAttachmentsRef.current = []

      // Snapshot the live page state bus so the server can inject it into the
      // system prompt. The currently-visible page publishes its state via
      // PageStateProvider → window.__atriumPageState.
      const pageStateSnapshot = typeof window !== 'undefined' ? (window as any).__atriumPageState : null
      // TODO(chat-reliability): publish real rendered metric values from the
      // campaigns/creatives charts and tables into window.__atriumVisibleMetrics.
      // This PR only wires the transport so get_page_visible_metrics can return
      // "no visible metrics" instead of letting the model invent numbers.
      const visibleMetricsSnapshot = typeof window !== 'undefined' ? (window as any).__atriumVisibleMetrics : null

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: apiMessages,
          sessionId,
          userMessageId,
          ...(endpointBody || {}),
          ...(activeWorkspaceId ? { workspaceId: activeWorkspaceId } : {}),
          ...(godMode ? { godMode: true } : {}),
          mode: toolMode,
          ...(threadModel ? { model: threadModel } : {}),
          ...(currentAttachments.length > 0 ? { attachments: currentAttachments.map(a => ({ id: a.id })) } : {}),
          ...(pageStateSnapshot ? { pageState: pageStateSnapshot } : {}),
          ...(visibleMetricsSnapshot ? { visible_metrics: visibleMetricsSnapshot } : {}),
        }),
        signal: controller.signal,
      })

      if (!response.ok) {
        // Try to surface the server's specific error message (e.g. daily
        // token budget exhausted, rate limit) instead of a generic
        // 'response was interrupted'. (Stephen, 2026-05-06: 429 from
        // daily_token_budget_exhausted was being shown as a generic
        // interrupt, looking like a bug.)
        let parsedError: { error?: string; message?: string; code?: string; retry_after_sec?: number; details?: unknown } | null = null
        try {
          parsedError = await response.clone().json()
        } catch {
          // not JSON — fall through to generic
        }
        if (
          response.status === 402 &&
          parsedError?.error === 'usage_limit_exceeded' &&
          isUsageLimitDetails(parsedError.details)
        ) {
          showUsageLimit(parsedError.details)
        }
        const detail =
          parsedError?.message
          ?? parsedError?.error
          ?? (response.status === 429 ? 'Rate limit reached. Please wait a moment and try again.' : null)
          ?? `Request failed: ${response.status}`
        const err = new Error(detail) as Error & { code?: string; status?: number }
        err.status = response.status
        if (parsedError?.code) err.code = parsedError.code
        throw err
      }
      requestUserChatActivityRefresh()
      if (!response.body) throw new Error('No response body')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let sseBuffer = ''

      const processSSE = (chunk: string) => {
        // Reset 120s silence watchdog on any incoming data
        if (streamTimeoutRef.current) clearTimeout(streamTimeoutRef.current)
        // Reset the 15s "still working..." timer on any incoming data so
        // it only fires when the model is actually quiet for >=15s. This
        // is a softer signal than the 120s watchdog — visible to the user
        // as a pulse, not a kill.
        if (streamSlowTimeoutRef.current) clearTimeout(streamSlowTimeoutRef.current)
        setStreamSlow(false)
        streamSlowTimeoutRef.current = setTimeout(() => setStreamSlow(true), 15_000)
        streamTimeoutRef.current = setTimeout(() => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort()
            abortControllerRef.current = null
          }
          setIsLoading(false)
          const retryMsg = lastUserMessageRef.current
          setMessages(prev => {
            const msgs = prev.map(m => m.isTyping ? { ...m, isTyping: false } : m)
            msgs.push({ role: 'assistant', content: 'The response timed out. Please try again.', isRetryable: true, retryMessage: retryMsg })
            return msgs
          })
        }, 120000)

        sseBuffer += chunk
        const parts = sseBuffer.split('\n\n')
        sseBuffer = parts.pop() ?? ''

        for (const part of parts) {
          const lines = part.split('\n')
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const dataStr = line.slice(6).trim()
            if (!dataStr) continue
            try {
              const event = JSON.parse(dataStr)

              if (event.type === 'text_delta' && typeof event.content === 'string') {
                // Fire receive sound on the first text_delta of this response
                if (!receiveSoundFiredRef.current) {
                  receiveSoundFiredRef.current = true
                  playReceiveSound()
                }
                streamingContent += event.content
                // Strip <options>...</options> and other control tags from displayed text
                const cleanContent = streamingContent
                  .replace(/<options>[\s\S]*?<\/options>/g, '')
                  .replace(/<attach_creative>[\s\S]*?<\/attach_creative>/g, '')
                  .replace(/<tool_call>[\s\S]*?<\/tool_call>/g, '')
                  .trim()
                setMessages(prev => {
                  const msgs = [...prev]
                  for (let idx = msgs.length - 1; idx >= 0; idx--) {
                    if (msgs[idx].role === 'assistant') {
                      const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                      if (segs.length > 0 && segs[segs.length - 1].type === 'text') {
                        const last = segs[segs.length - 1] as { type: 'text'; content: string }
                        segs[segs.length - 1] = { type: 'text', content: last.content + event.content }
                      } else {
                        segs.push({ type: 'text', content: event.content as string })
                      }
                      msgs[idx] = { ...msgs[idx], content: cleanContent, segments: segs }
                      break
                    }
                  }
                  return msgs
                })

              } else if (event.type === 'tool_call') {
                const toolKey = `${event.name as string}-${toolCallIndexRef.current++}`
                setToolCalls(prev => {
                  const next = new Map(prev)
                  next.set(toolKey, {
                    name: event.name as string,
                    input: (event.input as Record<string, unknown>) || {},
                    status: 'pending',
                  })
                  return next
                })
                // Push a tool segment onto the current assistant message
                setMessages(prev => {
                  const msgs = [...prev]
                  for (let idx = msgs.length - 1; idx >= 0; idx--) {
                    if (msgs[idx].role === 'assistant') {
                      const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                      segs.push({ type: 'tool', toolKey })
                      msgs[idx] = { ...msgs[idx], segments: segs }
                      break
                    }
                  }
                  return msgs
                })
                onUiEvent?.({ type: 'tool_call', name: event.name, data: event.input, timestamp: new Date() })

              } else if (event.type === 'tool_result') {
                const toolName = event.name as string
                if (toolName === 'create_campaign' && (event.result as Record<string, unknown>)?.campaign_id) {
                  setCampaignId((event.result as Record<string, unknown>).campaign_id as string)
                }
                if (toolName === 'create_video' && (event.result as any)?.job_id) {
                  setTimeout(() => {
                    const jobId = (event.result as any).job_id
                    navigateFromChat('/videos/' + jobId)
                  }, 1000)
                }
                // Handle sonobi_query — set pending query in context so the editor tab can pick it up
                if (toolName === 'sonobi_query' && (event.result as Record<string, unknown>)?.action === 'set_sonobi_query') {
                  const res = event.result as Record<string, unknown>
                  setPendingSonobiQuery({
                    query: res.query as string,
                    variables: res.variables as Record<string, unknown> | undefined,
                    autoExecute: false,
                  })
                }
                // Handle credits purchase — navigate to credits page
                if (toolName === 'add_wallet_funds' && (event.result as Record<string, unknown>)?.action === 'open_credits_page') {
                  setTimeout(() => {
                    navigateFromChat('/usage/plans')
                  }, 500)
                }
                const uaConversion = (event.result as Record<string, unknown>)?.ua_conversion as
                  | { event?: UAEventName; params?: Record<string, unknown> }
                  | null
                  | undefined
                if (uaConversion?.event && uaConversion.params) {
                  sendUAConversion(uaConversion.event, uaConversion.params as never)
                }
                // Page State Bus — dispatch any _ui_action on the result into the page state
                const uiAction = (event.result as Record<string, unknown>)?._ui_action as
                  | { type: string; path?: string; [key: string]: unknown }
                  | undefined
                if (uiAction && typeof uiAction === 'object' && typeof uiAction.type === 'string') {
                  // Special case: get_page_state is a read — no dispatch needed,
                  // but we backfill the tool result with the current snapshot so the
                  // model sees what the user is looking at.
                  if (uiAction.type === 'get_page_state') {
                    // No-op here; handled below when we mutate the tool result for the model
                  } else if (
                    // Accept both the canonical 'navigate_to_dashboard_view'
                    // and the legacy 'navigate_to_campaigns_view' so older
                    // server builds (or in-flight tool calls dispatched
                    // before this client deployed) still navigate cleanly.
                    // Drop the legacy literal once we're confident no
                    // server is still emitting it.
                    uiAction.type === 'navigate_to_dashboard_view' ||
                    uiAction.type === 'navigate_to_campaigns_view'
                  ) {
                    const { path, ...actionRest } = uiAction
                    void path
                    console.log('[chat] navigate_to_dashboard_view dispatch', actionRest)

                    const dispatch = () => {
                      const fn = (window as any).__atriumDispatchPageAction as
                        | ((a: unknown) => void)
                        | undefined
                      if (fn) {
                        fn(actionRest)
                      } else {
                        console.warn('[chat] no __atriumDispatchPageAction available')
                      }
                    }

                    // Always dispatch first — the page state bus will queue the
                    // action if no listener exists yet, and replay it when the
                    // target page subscribes.
                    dispatch()

                    // Then navigate (no-op if already on /campaigns). /overview
                    // is the primary summary route; this reporting action still
                    // targets campaign details.
                    if (
                      typeof window !== 'undefined' &&
                      !window.location.pathname.startsWith('/campaigns')
                    ) {
                      navigateFromChat('/campaigns')
                    }
                  } else if (uiAction.type === 'navigate_to_videos_view') {
                    const { path, ...actionRest } = uiAction
                    void path
                    const fn = (window as any).__atriumDispatchPageAction as ((a: unknown) => void) | undefined
                    if (fn) fn(actionRest)
                    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/videos')) {
                      navigateFromChat('/videos')
                    }
                  } else if (uiAction.type === 'navigate_to_video') {
                    const targetPath = (uiAction.path as string) || `/videos/${uiAction.videoId}`
                    navigateFromChat(targetPath)
                  } else if (uiAction.type === 'navigate_to_brand') {
                    const targetPath = (uiAction.path as string) || '/brand'
                    navigateFromChat(targetPath)
                  } else {
                    // Generic action — dispatch directly. Page state bus will
                    // buffer if no listener is registered yet.
                    const fn = (window as any).__atriumDispatchPageAction as
                      | ((a: unknown) => void)
                      | undefined
                    if (fn) fn(uiAction)
                  }
                }
                // Backfill get_page_state result with the live snapshot so the model
                // sees the current page context
                if (toolName === 'get_page_state') {
                  const snapshot = (window as any).__atriumPageState ?? null
                  ;(event as any).result = { ok: true, snapshot }
                }
                // Find the most recent pending tool call with this name
                setToolCalls(prev => {
                  const next = new Map(prev)
                  let found = false
                  // Iterate in reverse to find the most recent matching pending tool
                  const keys = Array.from(next.keys()).reverse()
                  for (const key of keys) {
                    const tc = next.get(key)!
                    if (tc.name === toolName && tc.status === 'pending') {
                      next.set(key, { ...tc, result: event.result, status: 'complete' })
                      found = true
                      break
                    }
                  }
                  if (!found) {
                    // fallback: just mark the first pending one of this name
                    for (const [key, tc] of next) {
                      if (tc.name === toolName && tc.status === 'pending') {
                        next.set(key, { ...tc, result: event.result, status: 'complete' })
                        break
                      }
                    }
                  }
                  return next
                })
                onUiEvent?.({ type: 'tool_result', name: event.name, data: event.result, timestamp: new Date() })

                // Dispatch refresh event for data-modifying tools so pages can live-update
                const mutatingTools = new Set([
                  'create_campaign', 'launch_campaign', 'pause_campaign', 'resume_campaign',
                  'update_campaign', 'delete_campaign', 'update_bid', 'update_pacing',
                  'allocate_campaign_budget', 'attach_creative', 'update_brand',
                  'update_campaign_alerts',
                  'add_wallet_funds', 'reallocate_budget',
                  'update_campaign_schedule', 'update_geo_targeting', 'update_frequency_cap',
                ])
                if (mutatingTools.has(event.name as string)) {
                  window.dispatchEvent(new CustomEvent('atrium-data-changed', {
                    detail: { tool: event.name, result: event.result },
                  }))
                }

              } else if (event.type === 'tool_receipt') {
                // Append tool receipt inline as part of the streaming content
                const receiptText = `\n\n${event.content as string}`
                streamingContent += receiptText
                setMessages(prev => {
                  const msgs = [...prev]
                  for (let idx = msgs.length - 1; idx >= 0; idx--) {
                    if (msgs[idx].role === 'assistant') {
                      const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                      segs.push({ type: 'text', content: receiptText })
                      msgs[idx] = { ...msgs[idx], content: streamingContent.trim(), segments: segs }
                      break
                    }
                  }
                  return msgs
                })

              } else if (event.type === 'thinking_start') {
                setMessages(prev => {
                  const msgs = [...prev]
                  for (let idx = msgs.length - 1; idx >= 0; idx--) {
                    if (msgs[idx].role === 'assistant') {
                      const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                      segs.push({ type: 'thinking', content: '', isStreaming: true })
                      msgs[idx] = { ...msgs[idx], segments: segs }
                      break
                    }
                  }
                  return msgs
                })

              } else if (event.type === 'thinking_delta') {
                setMessages(prev => {
                  const msgs = [...prev]
                  for (let idx = msgs.length - 1; idx >= 0; idx--) {
                    if (msgs[idx].role === 'assistant') {
                      const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                      for (let si = segs.length - 1; si >= 0; si--) {
                        if (segs[si].type === 'thinking') {
                          const ts = segs[si] as { type: 'thinking'; content: string; isStreaming?: boolean }
                          segs[si] = { ...ts, content: ts.content + (event.content as string) }
                          break
                        }
                      }
                      msgs[idx] = { ...msgs[idx], segments: segs }
                      break
                    }
                  }
                  return msgs
                })

              } else if (event.type === 'thinking_end') {
                setMessages(prev => {
                  const msgs = [...prev]
                  for (let idx = msgs.length - 1; idx >= 0; idx--) {
                    if (msgs[idx].role === 'assistant') {
                      const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                      for (let si = segs.length - 1; si >= 0; si--) {
                        if (segs[si].type === 'thinking') {
                          const ts = segs[si] as { type: 'thinking'; content: string; isStreaming?: boolean }
                          segs[si] = { ...ts, isStreaming: false }
                          break
                        }
                      }
                      msgs[idx] = { ...msgs[idx], segments: segs }
                      break
                    }
                  }
                  return msgs
                })

              } else if (event.type === 'navigate') {
                const path = event.path as string
                setTimeout(() => navigateFromChat(path), 500)

              } else if (event.type === 'options') {
                setMessages(prev => {
                  const msgs = [...prev]
                  // Find last assistant message
                  for (let idx = msgs.length - 1; idx >= 0; idx--) {
                    if (msgs[idx].role === 'assistant') {
                      msgs[idx] = { ...msgs[idx], options: event.options as string[] }
                      break
                    }
                  }
                  return msgs
                })

              } else if (event.type === 'session_refresh') {
                // Account was just created from anonymous session — soft refresh
                // to pick up new auth state without losing chat context
                const refreshSession = async () => {
                  try {
                    const { createClient } = await import('@/utils/supabase/client')
                    const supabase = createClient()
                    await supabase.auth.refreshSession()
                    // Soft refresh: re-run server components + middleware
                    // without destroying client state (chat stays open)
                    router.refresh()
                  } catch (err) {
                    console.error('[session_refresh] failed:', err)
                  }
                }
                refreshSession()

              } else if (event.type === 'brand_switched') {
                // Brand was switched — soft refresh to pick up new brand context
                router.refresh()

              } else if (event.type === 'meta') {
                if (event.campaignId && typeof event.campaignId === 'string') {
                  setCampaignId(event.campaignId)
                }
                onUiEvent?.({ type: 'meta', ...event })

              } else if (event.type === 'error') {
                if (event.error === 'usage_limit_exceeded' && isUsageLimitDetails(event.details)) {
                  showUsageLimit(event.details)
                }
                setIsLoading(false)
                const retryMsg = lastUserMessageRef.current
                const errorText = typeof event.message === 'string' && event.message.trim()
                  ? event.message.trim()
                  : 'Something went wrong. Please try again.'
                setMessages(prev => {
                  const msgs = prev.map(m => m.isTyping ? { ...m, isTyping: false } : m)
                  msgs.push({
                    role: 'assistant',
                    content: errorText,
                    isRetryable: !/not configured for this environment/i.test(errorText),
                    retryMessage: retryMsg,
                  })
                  return msgs
                })

              } else if (event.type === 'done') {
                setMessages(prev => {
                  const msgs = [...prev]
                  for (let idx = msgs.length - 1; idx >= 0; idx--) {
                    if (msgs[idx].role === 'assistant') {
                      const finalContent = streamingContent || msgs[idx].content
                      msgs[idx] = { ...msgs[idx], content: finalContent, isTyping: undefined }
                      break
                    }
                  }
                  return msgs
                })

                const tid = threadIdRef.current
                if (tid) {
                  persistAIMessage(tid, streamingContent)
                  if (extractEndpoint) {
                    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
                    const extractUrl = extractEndpoint
                    idleTimerRef.current = setTimeout(() => {
                      fetch(extractUrl, { method: 'POST' }).catch(() => {})
                    }, 600000)
                  }
                  if (isFirstAssistantResponse) {
                    setIsFirstAssistantResponse(false)
                    autoTitleThread(tid, effectiveMessage)
                  }
                  // Sherman Phase 1.1: refresh the sidebar so the new
                  // thread appears and existing ones get fresh previews
                  // / titles after the assistant reply lands.
                  bumpSidebar()
                }

                setIsLoading(false)
              }

            } catch {
              // ignore malformed SSE events
            }
          }
        }
      }

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        processSSE(decoder.decode(value, { stream: true }))
      }
      if (sseBuffer) processSSE('\n\n')

    } catch (err: any) {
      if (err?.name === 'AbortError') {
        // User cancelled — keep whatever was streamed so far
        setMessages(prev => prev.map(m => m.isTyping ? { ...m, isTyping: false } : m).filter(m => m.content.trim() !== ''))
      } else {
        const retryMsg = lastUserMessageRef.current
        // If the server sent a specific error (e.g. 429 daily budget
        // exhausted, rate limit), show that. Otherwise fall back to the
        // generic 'interrupted' banner.
        const errAny = err as { message?: string; status?: number; code?: string }
        const isServerError = !!errAny?.status && errAny.status >= 400
        const banner = isServerError && errAny?.message
          ? errAny.message
          : 'The response was interrupted. You can ask me to continue or try again.'
        // Don't offer retry on 429-budget; resetting at 00:00 UTC is the
        // only fix and re-trying would just 429 again.
        const retryable = !(errAny?.code === 'daily_token_budget_exhausted')
        setMessages(prev => {
          const msgs = prev.map(m => {
            if (m.isTyping) {
              return { ...m, isTyping: false, content: m.content || '' }
            }
            return m
          }).filter(m => m.content.trim() !== '')
          msgs.push({
            role: 'assistant',
            content: banner,
            isRetryable: retryable,
            retryMessage: retryable ? retryMsg : undefined,
          })
          return msgs
        })
      }
    } finally {
      if (streamTimeoutRef.current) { clearTimeout(streamTimeoutRef.current); streamTimeoutRef.current = null }
      if (streamSlowTimeoutRef.current) { clearTimeout(streamSlowTimeoutRef.current); streamSlowTimeoutRef.current = null }
      setStreamSlow(false)
      setIsLoading(false)
      abortControllerRef.current = null
    }
  }, [isLoading, messages, apiMessageTransform, endpoint, endpointBody, workspaceId, flowIntent, onThreadCreated, sessionId, extractEndpoint, isFirstAssistantResponse, navigateFromChat, onUiEvent, showUsageLimit]) // eslint-disable-line react-hooks/exhaustive-deps

  // From /home or /: send the seed message automatically.
  // sendMessage will create a thread if none exists; we send into the
  // existing thread for returning users, which appends the seed as a normal
  // user turn. (Previously this short-circuited on `messages.length > 0`,
  // which silently dropped seeds from the home composer for any returning
  // user with thread history.)
  useEffect(() => {
    if (!panelActive) return
    if (!autoSendSeed || !seedMessage) return
    if (isHydrating) return
    if (autoSendHandledRef.current) return
    if (!workspaceReady) return
    if (isLoading) return // streaming a response — wait for it to finish
    const seed = seedMessage.trim()
    const seedAlreadyInThread = messages.some(
      m => m.role === 'user' && m.content.trim() === seed,
    )
    autoSendHandledRef.current = true
    setInput('')
    onAutoSendConsumed?.()
    if (seedAlreadyInThread) return
    void sendMessage(seedMessage)
  }, [autoSendSeed, seedMessage, isHydrating, sendMessage, onAutoSendConsumed, panelActive, workspaceReady, isLoading, messages])

  const onboardingKickoffFiredForRef = useRef<string | null>(null)
  useEffect(() => {
    if (!panelActive) return
    if (!onboardingKickoffEndpoint) return
    if (!workspaceReady || isHydrating || isLoading) return
    if (seedMessage || autoSendSeed) return
    if (!internalThreadId) return
    if (messages.some(m => m.role === 'user' || (m.role === 'assistant' && m.content.trim()))) return
    if (onboardingKickoffFiredForRef.current === internalThreadId) return

    onboardingKickoffFiredForRef.current = internalThreadId
    setMessages([{ role: 'assistant', content: '', isTyping: true }])
    setIsLoading(true)
    receiveSoundFiredRef.current = false

    const controller = new AbortController()
    abortControllerRef.current = controller

    void (async () => {
      let streamingContent = ''
      try {
        const response = await fetch(onboardingKickoffEndpoint, {
          method: 'POST',
          signal: controller.signal,
        })
        if (!response.ok) {
          let parsedError: { error?: unknown; details?: unknown } | null = null
          try {
            parsedError = await response.clone().json()
          } catch {
            // not JSON — fall through to generic
          }
          if (
            response.status === 402 &&
            parsedError?.error === 'usage_limit_exceeded' &&
            isUsageLimitDetails(parsedError.details)
          ) {
            showUsageLimit(parsedError.details)
          }
          throw new Error(`Request failed: ${response.status}`)
        }
        requestUserChatActivityRefresh()
        if (!response.body) throw new Error('No response body')

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let sseBuffer = ''

        const processSSE = (chunk: string) => {
          if (streamTimeoutRef.current) clearTimeout(streamTimeoutRef.current)
          if (streamSlowTimeoutRef.current) clearTimeout(streamSlowTimeoutRef.current)
          setStreamSlow(false)
          streamSlowTimeoutRef.current = setTimeout(() => setStreamSlow(true), 15_000)
          streamTimeoutRef.current = setTimeout(() => {
            abortControllerRef.current?.abort()
            abortControllerRef.current = null
            setIsLoading(false)
            setMessages(prev => prev.map(m => m.isTyping ? { ...m, isTyping: false } : m).filter(m => m.content.trim() !== ''))
          }, 120000)

          sseBuffer += chunk
          const parts = sseBuffer.split('\n\n')
          sseBuffer = parts.pop() ?? ''

          for (const part of parts) {
            const lines = part.split('\n')
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue
              const dataStr = line.slice(6).trim()
              if (!dataStr) continue
              try {
                const event = JSON.parse(dataStr)

                if (event.type === 'text_delta' && typeof event.content === 'string') {
                  if (!receiveSoundFiredRef.current) {
                    receiveSoundFiredRef.current = true
                    playReceiveSound()
                  }
                  streamingContent += event.content
                  const cleanContent = streamingContent
                    .replace(/<options>[\s\S]*?<\/options>/g, '')
                    .replace(/<attach_creative>[\s\S]*?<\/attach_creative>/g, '')
                    .replace(/<tool_call>[\s\S]*?<\/tool_call>/g, '')
                    .trim()
                  setMessages(prev => {
                    const msgs = [...prev]
                    for (let idx = msgs.length - 1; idx >= 0; idx--) {
                      if (msgs[idx].role === 'assistant') {
                        const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                        if (segs.length > 0 && segs[segs.length - 1].type === 'text') {
                          const last = segs[segs.length - 1] as { type: 'text'; content: string }
                          segs[segs.length - 1] = { type: 'text', content: last.content + event.content }
                        } else {
                          segs.push({ type: 'text', content: event.content as string })
                        }
                        msgs[idx] = { ...msgs[idx], content: cleanContent, segments: segs }
                        break
                      }
                    }
                    return msgs
                  })
                } else if (event.type === 'tool_call') {
                  const toolKey = `${event.name as string}-${toolCallIndexRef.current++}`
                  setToolCalls(prev => {
                    const next = new Map(prev)
                    next.set(toolKey, {
                      name: event.name as string,
                      input: (event.input as Record<string, unknown>) || {},
                      status: 'pending',
                    })
                    return next
                  })
                  setMessages(prev => {
                    const msgs = [...prev]
                    for (let idx = msgs.length - 1; idx >= 0; idx--) {
                      if (msgs[idx].role === 'assistant') {
                        const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                        segs.push({ type: 'tool', toolKey })
                        msgs[idx] = { ...msgs[idx], segments: segs }
                        break
                      }
                    }
                    return msgs
                  })
                  onUiEvent?.({ type: 'tool_call', name: event.name, data: event.input, timestamp: new Date() })
                } else if (event.type === 'tool_result') {
                  const toolName = event.name as string
                  setToolCalls(prev => {
                    const next = new Map(prev)
                    const keys = Array.from(next.keys()).reverse()
                    for (const key of keys) {
                      const tc = next.get(key)!
                      if (tc.name === toolName && tc.status === 'pending') {
                        next.set(key, { ...tc, result: event.result, status: 'complete' })
                        break
                      }
                    }
                    return next
                  })
                  onUiEvent?.({ type: 'tool_result', name: event.name, data: event.result, timestamp: new Date() })
                } else if (event.type === 'thinking_start') {
                  setMessages(prev => {
                    const msgs = [...prev]
                    for (let idx = msgs.length - 1; idx >= 0; idx--) {
                      if (msgs[idx].role === 'assistant') {
                        const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                        segs.push({ type: 'thinking', content: '', isStreaming: true })
                        msgs[idx] = { ...msgs[idx], segments: segs }
                        break
                      }
                    }
                    return msgs
                  })
                } else if (event.type === 'thinking_delta') {
                  setMessages(prev => {
                    const msgs = [...prev]
                    for (let idx = msgs.length - 1; idx >= 0; idx--) {
                      if (msgs[idx].role === 'assistant') {
                        const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                        for (let si = segs.length - 1; si >= 0; si--) {
                          if (segs[si].type === 'thinking') {
                            const ts = segs[si] as { type: 'thinking'; content: string; isStreaming?: boolean }
                            segs[si] = { ...ts, content: ts.content + (event.content as string) }
                            break
                          }
                        }
                        msgs[idx] = { ...msgs[idx], segments: segs }
                        break
                      }
                    }
                    return msgs
                  })
                } else if (event.type === 'thinking_end') {
                  setMessages(prev => {
                    const msgs = [...prev]
                    for (let idx = msgs.length - 1; idx >= 0; idx--) {
                      if (msgs[idx].role === 'assistant') {
                        const segs: MessageSegment[] = msgs[idx].segments ? [...msgs[idx].segments!] : []
                        for (let si = segs.length - 1; si >= 0; si--) {
                          if (segs[si].type === 'thinking') {
                            const ts = segs[si] as { type: 'thinking'; content: string; isStreaming?: boolean }
                            segs[si] = { ...ts, isStreaming: false }
                            break
                          }
                        }
                        msgs[idx] = { ...msgs[idx], segments: segs }
                        break
                      }
                    }
                    return msgs
                  })
                } else if (event.type === 'navigate') {
                  setTimeout(() => navigateFromChat(event.path as string), 500)
                } else if (event.type === 'options') {
                  setMessages(prev => {
                    const msgs = [...prev]
                    for (let idx = msgs.length - 1; idx >= 0; idx--) {
                      if (msgs[idx].role === 'assistant') {
                        msgs[idx] = { ...msgs[idx], options: event.options as string[] }
                        break
                      }
                    }
                    return msgs
                  })
                } else if (event.type === 'meta') {
                  onUiEvent?.({ type: 'meta', ...event })
                } else if (event.type === 'error') {
                  if (event.error === 'usage_limit_exceeded' && isUsageLimitDetails(event.details)) {
                    showUsageLimit(event.details)
                  }
                  setMessages(prev => prev.map(m => m.isTyping ? { ...m, isTyping: false } : m).filter(m => m.content.trim() !== ''))
                  setIsLoading(false)
                } else if (event.type === 'done') {
                  setMessages(prev => {
                    const msgs = [...prev]
                    for (let idx = msgs.length - 1; idx >= 0; idx--) {
                      if (msgs[idx].role === 'assistant') {
                        const finalContent = streamingContent || msgs[idx].content
                        if (!finalContent.trim() && !msgs[idx].segments?.length) {
                          msgs.splice(idx, 1)
                        } else {
                          msgs[idx] = { ...msgs[idx], content: finalContent, isTyping: undefined }
                        }
                        break
                      }
                    }
                    return msgs
                  })
                  setIsFirstAssistantResponse(false)
                  setIsLoading(false)
                }
              } catch {
                // ignore malformed SSE events
              }
            }
          }
        }

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          processSSE(decoder.decode(value, { stream: true }))
        }
        if (sseBuffer) processSSE('\n\n')
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          setMessages(prev => prev.map(m => m.isTyping ? { ...m, isTyping: false } : m).filter(m => m.content.trim() !== ''))
        }
      } finally {
        if (streamTimeoutRef.current) { clearTimeout(streamTimeoutRef.current); streamTimeoutRef.current = null }
        if (streamSlowTimeoutRef.current) { clearTimeout(streamSlowTimeoutRef.current); streamSlowTimeoutRef.current = null }
        setStreamSlow(false)
        setIsLoading(false)
        abortControllerRef.current = null
      }
    })()
  }, [panelActive, onboardingKickoffEndpoint, workspaceReady, isHydrating, isLoading, seedMessage, autoSendSeed, internalThreadId, messages, navigateFromChat, onUiEvent, showUsageLimit])

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort()
  }, [])

  // ─── Wire slash command UI hooks ─────────────────────────────────────────
  // The slashCommandContext is created near the top of this component (before
  // these callbacks exist). We populate the ref each render so the context can
  // dispatch ui-action results back to the host.
  useEffect(() => {
    slashCommandUiHooksRef.current = {
      startNewThread: () => { void startNewSession() },
      stopStreaming,
      // For now, side questions just get sent as a normal message. A future
      // iteration can route them to a separate side-thread.
      submitSideQuestion: (prompt: string) => { void sendMessage(prompt) },
    }
  }, [startNewSession, stopStreaming, sendMessage])

  // ─── Resend edited message (Fix 20) ──────────────────────────────────────

  const handleResend = useCallback((messageIndex: number, newContent: string) => {
    if (isLoading) return
    const truncated = messages.slice(0, messageIndex)
    void sendMessage(newContent, truncated)
  }, [isLoading, messages, sendMessage])

  const handleRetry = useCallback((messageIndex: number) => {
    let userMsg: string | null = null
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        userMsg = messages[i].content
        break
      }
    }
    if (!userMsg) return
    setMessages(prev => prev.slice(0, messageIndex))
    void sendMessage(userMsg)
  }, [messages, sendMessage])

  // ─── Slash command UI dispatch ──────────────────────────────────────────────
  // Most slash command results render via SlashCommandResultCard (success/error
  // /navigate). `ui-action` results are dispatched here because they touch host
  // state (streaming controllers, threads, external windows).
  const handleSlashUiAction = useCallback((action: SlashCommandUiAction) => {
    switch (action.type) {
      case 'new-thread':
        void startNewSession()
        return
      case 'stop-streaming':
        stopStreaming()
        return
      case 'open-checkout':
        // Open Stripe Checkout in a new tab so the chat thread is preserved.
        if (typeof window !== 'undefined') window.open(action.url, '_blank', 'noopener,noreferrer')
        return
      case 'open-help':
        // Phase 2: navigate to /help for now; Phase 5 will swap in a slide-out panel.
        navigateFromChat('/help')
        return
      default: {
        const _exhaustive: never = action
        return _exhaustive
      }
    }
  }, [navigateFromChat, startNewSession, stopStreaming])

  // ─── Input area ─────────────────────────────────────────────────────────────

  const animatedInputPlaceholder = useMarketingAnimatedPlaceholder(
    variant !== 'panel' && !mobileLayout && panelActive && !hasContent
  )
  const inputPlaceholder =
    variant === 'panel' || mobileLayout
      ? 'Plan, Launch, Manage, / for commands, @ for context'
      : animatedInputPlaceholder

  // Auto-focus input when chat panel opens and messages finish loading
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const composerInputId = useId()

  useEffect(() => {
    if (!panelActive) return
    if (!isHydrating && !isLoading) {
      const timer = setTimeout(() => {
        const el = document.querySelector<HTMLTextAreaElement>('[data-chat-input]')
        if (el) el.focus()
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [isHydrating, isLoading, panelActive])

  const hasMessages = messages.length > 0 && messages.some(m => m.role === 'user' || (m.role === 'assistant' && m.content.trim()))

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  const removePendingAttachment = useCallback((id: string) => {
    setPendingAttachments(prev => {
      const toRemove = prev.find(a => a.id === id)
      if (toRemove?.previewUrl) URL.revokeObjectURL(toRemove.previewUrl)
      return prev.filter(a => a.id !== id)
    })
    pendingAttachmentsRef.current = pendingAttachmentsRef.current.filter(a => a.id !== id)
  }, [])

  const chatUploadAccept =
    '.csv,.txt,.pdf,.png,.jpg,.jpeg,.gif,.webp,.mp4,.mov,.webm'

  // ─── Shared inner layout ─────────────────────────────────────────────────────

  // Sherman Phase 1.1 — thread sidebar wiring.
  // Refresh ticker: bump every time a new message lands or a thread is
  // touched so the sidebar refetches and reflects updated titles/previews.
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0)
  const bumpSidebar = useCallback(() => setSidebarRefreshKey(k => k + 1), [])

  // 'New chat' from the sidebar — clear local state, do NOT archive the
  // existing thread (user reaches it via the sidebar). Pre-v2 'New
  // Conversation' archives + persists a divider; v2 doesn't need either.
  const startNewThreadV2 = useCallback(() => {
    setMessages([])
    setToolCalls(new Map())
    setInternalThreadId(null)
    threadIdRef.current = null
    setThreadTitle('Chat')
    setThreadModel(null)
    setIsFirstAssistantResponse(true)
    seedSentRef.current = false
    autoSendHandledRef.current = false
    toolCallIndexRef.current = 0
    setPendingAttachments([])
    pendingAttachmentsRef.current = []
    setHasMore(false)
    setOldestCreatedAt(null)
    setBookmarkedMessageIds(new Set())
    setBookmarkPreviews([])
    setQuotedTexts([])
    bumpSidebar()
  }, [bumpSidebar])

  // 'Select thread' from the sidebar — load the picked thread's messages.
  // Hydration here is intentionally minimal — the full hydration path in
  // getOrCreateThread does tool-call segment reconstruction. Switching
  // threads rehydrates plain text; tool segments come back on next mount
  // when getOrCreateThread runs for that thread. Acceptable for v0; a
  // follow-up factors out a shared hydrate() helper.
  const selectThreadV2 = useCallback(async (threadId: string) => {
    if (threadId === internalThreadId) return
    setMessages([])
    setToolCalls(new Map())
    setInternalThreadId(threadId)
    threadIdRef.current = threadId
    setIsFirstAssistantResponse(false)
    seedSentRef.current = false
    autoSendHandledRef.current = false
    toolCallIndexRef.current = 0
    setPendingAttachments([])
    pendingAttachmentsRef.current = []
    setBookmarkedMessageIds(new Set())
    setBookmarkPreviews([])
    setQuotedTexts([])
    setIsHydrating(true)
    try {
      const res = await fetch(`/api/chat/threads/${threadId}/messages?limit=50`)
      if (res.ok) {
        const data = await res.json()
        const rows = (data.messages ?? []) as Array<{ id?: string; role: string; content: string; created_at?: string; metadata?: { kickoff?: boolean } }>
        const filtered = rows.filter(m =>
          !(m.role === 'system' && ((m.content ?? '').startsWith('[tool_receipt]') || /^[\u{1F300}-\u{1FAD6}]/u.test(m.content ?? '')))
          && !(m.role === 'user' && m.metadata?.kickoff === true)
        )
        setMessages(filtered.map(m => ({
          id: m.id,
          role: m.role as 'user' | 'assistant' | 'system',
          content: m.content,
          created_at: m.created_at,
        })))
      }
      // Fetch metadata to update title and model. Sidebar already has
      // these but it's cheap to be explicit and avoids a prop drill.
      const tRes = await fetch(`/api/chat/threads?workspaceId=${workspaceId ?? ''}&mode=${chatMode}&limit=50`)
      if (tRes.ok) {
        const tData = await tRes.json()
        const found = (tData.threads ?? []).find((t: { id: string; title: string; model?: string | null }) => t.id === threadId)
        if (found) {
          setThreadTitle(found.title || 'Chat')
          setThreadModel(found.model ?? null)
          onThreadCreated?.(found.id, found.title)
        }
      }
    } finally {
      setIsHydrating(false)
    }
    bumpSidebar()
  }, [internalThreadId, workspaceId, chatMode, onThreadCreated, bumpSidebar])

  // Sidebar render helper — wraps `inner` with the sidebar on the left
  // when v2 is on. The width class differs per variant so the chat column
  // still has breathing room in narrow panel views.
  const renderWithSidebar = (inner: React.ReactNode, sidebarWidthClass: string) => {
    if (!chatThreadsV2 || mobileLayout) return inner
    return (
      <div className="flex h-full min-h-0 w-full min-w-0">
        <aside className={cn('shrink-0 border-r border-atrium-border min-h-0 h-full', sidebarWidthClass)}>
          <ChatThreadSidebar
            workspaceId={workspaceId ?? null}
            mode={chatMode}
            activeThreadId={internalThreadId}
            onSelectThread={selectThreadV2}
            onNewChat={startNewThreadV2}
            refreshKey={sidebarRefreshKey}
          />
        </aside>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {inner}
        </div>
      </div>
    )
  }

  const chatInner = (
    <div className="flex flex-col h-full min-h-0 relative">
      <div className={cn('h-16 pb-2 w-full shrink-0 grid grid-cols-[1fr_auto_1fr] items-center px-4', shellBgClass)}>
        <div className="flex justify-start min-w-0">
          {variant === 'panel' && panelViewportFullscreen && onMenuToggle ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 rounded-full shrink-0"
                  onClick={onMenuToggle}
                  aria-label="Open navigation menu"
                >
                  <HugeiconsIcon icon={Menu01Icon} className="size-5 text-atrium-black" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Navigation</TooltipContent>
            </Tooltip>
          ) : null}
          {variant === 'panel' && onPanelCollapse ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 rounded-full shrink-0"
                  onClick={() => onPanelCollapse()}
                  aria-label="Collapse chat"
                >
                  <HugeiconsIcon icon={SidebarRightIcon} className="size-5 text-atrium-black" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Collapse chat
              </TooltipContent>
            </Tooltip>
          ) : null}
          {variant === 'fullscreen' && !mobileLayout && onMenuToggle ? (
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center group/menu">
              <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-150 group-hover/menu:opacity-0">
                <Logo variant="mark" width={24} height={24} className="size-6 -mt-0.5" />
              </div>
              <button
                type="button"
                onClick={onMenuToggle}
                aria-label="Toggle sidebar"
                className="absolute inset-0 flex items-center justify-center rounded-lg cursor-pointer opacity-0 transition-opacity duration-150 hover:bg-atrium-content group-hover/menu:opacity-100"
              >
                <HugeiconsIcon icon={Menu01Icon} className="size-5 text-atrium-black" />
              </button>
            </div>
          ) : null}
          {variant === 'panel' && onPanelViewportFullscreenChange && (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 rounded-full shrink-0"
                  onClick={() => onPanelViewportFullscreenChange(!panelViewportFullscreen)}
                  aria-label={panelViewportFullscreen ? 'Exit fullscreen' : 'Expand chat'}
                >
                  <HugeiconsIcon
                    icon={panelViewportFullscreen ? ArrowShrink02Icon : ArrowExpand02Icon}
                    className="size-5 text-atrium-black"
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {panelViewportFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              </TooltipContent>
            </Tooltip>
          )}
          {variant === 'fullscreen' && onCollapse ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 rounded-full shrink-0"
                  onClick={onCollapse}
                  aria-label={mobileLayout ? 'Back' : 'Exit fullscreen'}
                >
                  <HugeiconsIcon
                    icon={mobileLayout ? ArrowLeft01Icon : ArrowShrink02Icon}
                    className="size-5 text-atrium-black"
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{mobileLayout ? 'Back' : 'Exit fullscreen'}</TooltipContent>
            </Tooltip>
          ) : null}
          {/* Workspace switcher removed 2026-05-13 (Stephen): the chat always
              uses the current workspace context — no in-header picker. Use
              the main sidebar switcher to change workspaces. */}
        </div>
        <button
          type="button"
          onClick={() => {
            // Exit fullscreen first (if active) so the destination page renders cleanly,
            // then navigate. The pathname effect in ChatContext will close the chat
            // panel state for non-/chat routes.
            if (panelViewportFullscreen && onPanelViewportFullscreenChange) {
              onPanelViewportFullscreenChange(false)
            }
            router.push('/campaigns')
          }}
          className="flex items-center gap-1.5 rounded-lg px-1 py-0.5 -mx-1 transition-colors hover:bg-atrium-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-atrium-black/10"
          aria-label="Go to campaigns"
        >
          <div className={`h-7 w-7 rounded-full flex items-center justify-center drop-shadow-sm bg-linear-to-br ${godMode ? 'from-amber-500 to-orange-600' : 'from-accent-600 to-accent-700'}`}></div>
          <div className="flex flex-col items-start min-w-0">
            <h1 className="text-base font-semibold text-atrium-black text-center truncate min-w-0 leading-tight">
              Atrium
            </h1>
            {godMode && (
              <span className="text-[10px] font-medium uppercase tracking-wider text-amber-600 leading-tight">
                God mode enabled
              </span>
            )}
          </div>
        </button>
        <div className="flex items-center gap-0.5 justify-end">
          {/* Bookmarks dropdown */}
          <Popover open={bookmarksOpen} onOpenChange={setBookmarksOpen}>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 rounded-full">
                    <HugeiconsIcon icon={Bookmark02Icon} className="size-5 text-atrium-black" />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <span>Bookmarks</span>
              </TooltipContent>
            </Tooltip>
            {/* z-[200] beats the chat shell at z-[110] in CampaignChatPanel.
                Default PopoverContent z-50 portals to <body> but loses to the
                fixed chat shell. Stephen 2026-05-13. */}
            <PopoverContent align="end" className="z-[200] w-72 p-2">
              <p className="text-[11px] font-semibold text-atrium-muted uppercase tracking-wide px-2 py-1 mb-1">Bookmarks</p>
              {bookmarkPreviews.length === 0 ? (
                <p className="text-xs text-[#939294] px-2 py-3 text-center">No bookmarks yet</p>
              ) : (
                <div className="space-y-0.5 max-h-72 overflow-y-auto">
                  {bookmarkPreviews.map(b => (
                    <button
                      key={b.messageId}
                      onClick={() => {
                        const el = messageElementsRef.current.get(b.messageId)
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        } else {
                          toast('Message may be in older history — scroll up to load more')
                        }
                        setBookmarksOpen(false)
                      }}
                      className="w-full text-left flex flex-col gap-0.5 px-2.5 py-2 rounded-lg hover:bg-atrium-content transition-colors"
                    >
                      <span className="text-xs text-atrium-black line-clamp-2">{b.preview || '(no preview)'}</span>
                      {b.created_at && (
                        <span className="text-[10px] text-[#939294]">{assistantMessageTimeShort(b.created_at)}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* More options dropdown */}
          <Popover>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 rounded-full"
                  >
                    <HugeiconsIcon icon={MoreHorizontalIcon} className="size-5 text-atrium-black" />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <span>More options</span>
              </TooltipContent>
            </Tooltip>
            {/* z-[200] beats the chat shell at z-[110] in CampaignChatPanel.
                Default PopoverContent z-50 portals to <body> but loses to the
                fixed chat shell. Stephen 2026-05-13. */}
            <PopoverContent align="end" className="z-[200] w-52 p-2">
              <div className="flex items-center justify-between gap-3 px-2 py-1.5">
                <span className="text-sm text-atrium-black">Sound Effects</span>
                <Switch
                  checked={!soundsMuted}
                  onCheckedChange={(checked) => {
                    const muted = !checked
                    setSoundsMuted(muted)
                    localStorage.setItem('monarch.sounds_muted', muted ? 'true' : 'false')
                  }}
                />
              </div>
              <div className="my-1 border-t border-atrium-border" />
              <button
                onClick={exportChat}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-atrium-content text-left"
              >
                <HugeiconsIcon icon={Download01Icon} className="size-4 text-atrium-black/60" />
                Export chat
              </button>
              {headerActions && headerActions.length > 0 && (
                <>
                  <div className="my-1 border-t border-atrium-border" />
                  {headerActions.map((action, i) => (
                    action.href ? (
                      <a
                        key={i}
                        href={action.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex w-full items-center rounded-md px-2 py-1.5 text-sm hover:bg-atrium-content"
                      >
                        {action.label}
                      </a>
                    ) : (
                      <button
                        key={i}
                        onClick={action.onClick}
                        className="flex w-full items-center rounded-md px-2 py-1.5 text-sm hover:bg-atrium-content text-left"
                      >
                        {action.label}
                      </button>
                    )
                  ))}
                </>
              )}
              {isAdmin && (
                <>
                  <div className="my-1 border-t border-atrium-border" />
                  <div className="flex items-center justify-between gap-3 px-2 py-1.5">
                    <span className="text-sm text-atrium-black">God Mode</span>
                    <Switch
                      checked={godMode}
                      onCheckedChange={(checked) => setGodMode(checked)}
                    />
                  </div>
                </>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>
      
      <div className="flex items-center justify-between px-3 h-8 shrink-0 bg-linear-to-b/oklab from-atrium-chatbar to-atrium-chatbar/0 absolute top-16 left-0 right-2.5 z-10 pointer-events-none"></div>

      {/* Empty state — centered input with suggestions (fullscreen / fullpage only; panel uses normal chrome + input) */}
      {!hasMessages && !isLoading && !isHydrating && variant !== 'panel' ? (
        <div className="flex-1 flex flex-col items-center justify-center min-h-0">
          <div className={cn(CHAT_EMPTY_STATE_CLASS, 'space-y-4')}>
            <ChatPromptBox
              inputRef={inputRef}
              fileInputRef={fileInputRef}
              composerInputId={composerInputId}
              input={input}
              onInputChange={setInput}
              onKeyDown={handleInputKeyDown}
              placeholder="Ask me to do anything..."
              minRows={2}
              maxRows={4}
              hasContent={hasContent}
              isLoading={isLoading}
              onSend={() => sendMessage(input)}
              onStop={stopStreaming}
              onAttachClick={() => fileInputRef.current?.click()}
              showNewSessionButton={false}
              onFileChange={handleFileSelect}
              accept={chatUploadAccept}
              attachDisabled={isUploading}
              attachments={pendingAttachments.map(a => ({
                id: a.id,
                name: a.file_name,
                size: a.file_size,
              }))}
              onRemoveAttachment={removePendingAttachment}
              slashCommandContext={slashCommandContext}
              includeAdminSlashCommands={isAdmin}
              onSlashLLMRouted={(prompt) => void sendMessage(prompt)}
              onSlashNavigate={(path) => {
                navigateFromChat(path)
              }}
              onSlashUiAction={handleSlashUiAction}
              promptControls={promptControls}
              autoFocus
            />
            {isUploading && (
              <div className="flex flex-wrap gap-1.5 justify-center">
                <div className="flex items-center gap-1 px-2.5 py-1 bg-neutral-100 rounded-full text-xs text-atrium-muted">
                  <span>Uploading…</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
      <>
      {/* Messages */}
      <div className="relative flex-1 min-h-0 flex flex-col">

        <div
          ref={messagesScrollRef}
          onScroll={updateScrollToBottomVisibility}
          className="chat-message-scroll flex-1 overflow-y-auto space-y-4 min-h-0 pt-12 pb-6"
        >
        {/* Messages list wrapper stays full width when in panel viewport-
            fullscreen so group-hover rows span the entire viewport. The
            inner content of each message component caps its own width via
            the `narrow` prop. */}
        <div className={messageListColumnClass}>
        {toolMode === 'ask' && !isHydrating ? (
          <div className={cn('mb-3 flex', narrowMessageColumn && CHAT_MESSAGE_NARROW_CLASS)}>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-atrium-border bg-atrium-background/80 px-2.5 py-1 text-[11px] font-medium text-atrium-black/55">
              <HugeiconsIcon icon={SearchIcon} className="size-3.5" />
              <span>Read-only — switch to Agent to take actions</span>
            </div>
          </div>
        ) : null}
        {isHydrating ? (
          <div className="flex items-center justify-center py-8">
            <span className="flex gap-1">
              <span className="w-1.5 h-1.5 bg-atrium-black/50 rounded-full animate-bounce [animation-delay:-0.3s]" />
              <span className="w-1.5 h-1.5 bg-atrium-black/50 rounded-full animate-bounce [animation-delay:-0.15s]" />
              <span className="w-1.5 h-1.5 bg-atrium-black/50 rounded-full animate-bounce" />
            </span>
          </div>
        ) : (
          <>
            {/* Infinite scroll sentinel — becomes visible when scrolled to top */}
            <div ref={loadMoreSentinelRef} />
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-atrium-black/50 rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-1.5 h-1.5 bg-atrium-black/50 rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-1.5 h-1.5 bg-atrium-black/50 rounded-full animate-bounce" />
                </span>
              </div>
            )}
            {messages.map((msg, i) => {
              if (!msg) return null
              const isLastMsg = i === messages.length - 1
              const isLastAssistant = msg.role === 'assistant' && (() => {
                for (let j = messages.length - 1; j >= 0; j--) {
                  if (messages[j]?.role === 'assistant') return j === i
                }
                return false
              })()
              const showOptions = (!msg.isTyping && msg.role === 'assistant' && isLastMsg && !isLoading && msg.options && msg.options.length > 0)

              // Session divider — rendered between old and new session messages
              if (msg.isDivider) {
                const dividerRow = (
                  <div data-session-divider className="flex items-center gap-3 py-4">
                    <div className="flex-1 h-px bg-atrium-border" />
                    <span className="text-[11px] text-[#939294] font-medium shrink-0 px-1">New session</span>
                    <div className="flex-1 h-px bg-atrium-border" />
                  </div>
                )
                return narrowMessageColumn ? (
                  <div key={i} className={CHAT_MESSAGE_NARROW_CLASS}>
                    {dividerRow}
                  </div>
                ) : (
                  <div key={i}>{dividerRow}</div>
                )
              }

              // System message (wallet deposits, automation notifications)
              if (msg.role === 'system') {
                // Spacer — pushes old session messages up so new session starts at bottom
                if (msg.content === '__spacer__') {
                  return <div key={i} className="min-h-[60vh]" />
                }
                return (
                  <div key={i} className="flex justify-center py-1">
                    <div className="flex items-center gap-1.5 rounded-full bg-atrium-content px-3 py-1.5">
                      <span className="size-1.5 shrink-0 rounded-full bg-atrium-black/40" />
                      <span className="text-xs text-[#939294]">{msg.content}</span>
                    </div>
                  </div>
                )
              }

              if (msg.role === 'user') {
                return (
                  <ChatUserMessage
                    key={i}
                    messageIndex={i}
                    quotedTexts={msg.quotedTexts}
                    content={msg.content}
                    channel={msg.channel}
                    attachments={msg.attachments}
                    onResend={handleResend}
                    narrow={narrowMessageColumn}
                  />
                )
              }

              // Assistant message
              return (
                <div key={i}>
                  <ChatAssistantMessage
                    narrow={narrowMessageColumn}
                    messageIndex={i}
                    messageId={msg.id}
                    bookmarkDisabled={!msg.id}
                    content={msg.content}
                    isTyping={msg.isTyping}
                    streamSlow={msg.isTyping ? streamSlow : false}
                    segments={msg.segments}
                    toolCalls={toolCalls}
                    created_at={msg.created_at}
                    rootRef={
                      msg.id
                        ? (el) => {
                            if (el) messageElementsRef.current.set(msg.id!, el)
                            else messageElementsRef.current.delete(msg.id!)
                          }
                        : undefined
                    }
                    onMouseEnter={() => setHoveredMsgIdx(i)}
                    onMouseLeave={() => setHoveredMsgIdx(null)}
                    showActions={hoveredMsgIdx === i || isLastAssistant}
                    bookmarkedMessageIds={bookmarkedMessageIds}
                    onToggleBookmark={toggleBookmark}
                    onCopy={() => {
                      navigator.clipboard.writeText(msg.content)
                      toast.success('Copied to clipboard')
                      setCopiedMsgIdx(i)
                      setTimeout(() => setCopiedMsgIdx(null), 1500)
                    }}
                    onCopyWithToolCalls={(transcript) => {
                      navigator.clipboard.writeText(transcript)
                      toast.success('Copied message + tool calls')
                      setCopiedMsgIdx(i)
                      setTimeout(() => setCopiedMsgIdx(null), 1500)
                    }}
                    onThumbsUp={() => {
                      setFeedbackIdx(i)
                      setFeedbackRating('positive')
                      setFeedbackText('')
                    }}
                    onThumbsDown={() => {
                      setFeedbackIdx(i)
                      setFeedbackRating('negative')
                      setFeedbackText('')
                    }}
                    onLinkQuote={(url, text) => setQuotedTexts(prev => [...prev, { text: text || url }])}
                    feedbackOpen={feedbackIdx === i && feedbackRating !== null}
                    feedbackRating={feedbackRating}
                    feedbackText={feedbackText}
                    onFeedbackTextChange={setFeedbackText}
                    onFeedbackClose={() => setFeedbackIdx(null)}
                    onFeedbackSubmit={async () => {
                      setSubmittingFeedback(true)
                      try {
                        const prevUserMsg = messages.slice(0, i).reverse().find(m => m.role === 'user')
                        const msgToolCalls =
                          msg.segments
                            ?.filter((s): s is { type: 'tool'; toolKey: string } => s.type === 'tool')
                            .map(s => {
                              const tc = toolCalls.get(s.toolKey)
                              if (!tc) return null
                              return { name: tc.name, input: tc.input, result: tc.result, status: tc.status }
                            })
                            .filter((tc): tc is NonNullable<typeof tc> => tc !== null) ?? []
                        await fetch('/api/chat/feedback', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            rating: feedbackRating,
                            feedback_text: feedbackText || null,
                            message_content: msg.content,
                            thread_id: internalThreadId,
                            user_message: prevUserMsg?.content || null,
                            tool_calls: msgToolCalls.length > 0 ? msgToolCalls : null,
                          }),
                        })
                        toast.success('Feedback submitted — thank you!')
                        setFeedbackIdx(null)
                      } catch {
                        toast.error('Failed to submit feedback')
                      } finally {
                        setSubmittingFeedback(false)
                      }
                    }}
                    submittingFeedback={submittingFeedback}
                    isRetryable={!!(msg.isRetryable && msg.retryMessage && !isLoading)}
                    onRetry={
                      msg.retryMessage && !isLoading
                        ? () => void sendMessage(msg.retryMessage!)
                        : undefined
                    }
                  />
                  {/* Regenerate button hidden — tool calls may have side effects (campaign changes, etc.)
                     Re-enable when we can detect if the response had write-tool calls.
                  {isLastAssistant && !isLoading && msg.content && (
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => handleRetry(i)}
                          className="ml-2 mt-0.5 rounded p-1 text-[#939294] transition-colors hover:text-atrium-black"
                          aria-label="Regenerate response"
                        >
                          <HugeiconsIcon icon={RotateLeft01Icon} className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs">Regenerate</TooltipContent>
                    </Tooltip>
                  )}
                  */}
                </div>
              )
            })}

          </>
        )}
        <div ref={messagesEndRef} />
        </div>
        </div>

        {showScrollToBottom && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
                }}
                className="absolute bottom-6 left-1/2 z-20 flex size-9 -translate-x-1/2 items-center justify-center rounded-full border border-atrium-border bg-atrium-inner/95 text-atrium-black/70 shadow-md backdrop-blur-sm transition-colors hover:bg-atrium-content hover:text-atrium-black"
                aria-label="Scroll to latest messages"
              >
                <HugeiconsIcon icon={ArrowDown02Icon} className="size-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">Latest messages</TooltipContent>
          </Tooltip>
        )}

        {/* Floating quote button — appears above text selection in assistant messages */}
        {quoteButton && (
          <button
            data-quote-button
            className="fixed z-[9999] flex items-center gap-1.5 h-9 bg-atrium-inner border border-atrium-border text-atrium-black text-xs font-medium px-3 rounded-full shadow-lg hover:bg-atrium-content transition-colors pointer-events-auto"
            style={{
              left: quoteButton.x,
              top: quoteButton.y,
              transform: 'translateX(-50%) translateY(-100%)',
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              setQuotedTexts(prev => [...prev, { text: quoteButton.text, messageIndex: quoteButton.messageIndex }])
              setQuoteButton(null)
              window.getSelection()?.removeAllRanges()
              setTimeout(() => inputRef.current?.focus(), 0)
            }}
          >
            <HugeiconsIcon icon={QuoteDownIcon} className="size-4 shrink-0" />
            Quote
          </button>
        )}
      </div>

      {/* Pending options — shown above input */}
      {(() => {
        const lastMsg = messages[messages.length - 1]
        if (!isLoading && !isHydrating && lastMsg?.role === 'assistant' && !lastMsg.isTyping && lastMsg.options && lastMsg.options.length > 0) {
          const labels = 'ABCDEFGHIJ'
          return (
            <div className={cn('shrink-0 pt-3 pb-2 z-40 relative', shellBgClass)}>
              {/* narrowMessageColumn is now always-on in panel + the
                  panel-fullscreen overlay, and fullpage/fullscreen pages
                  also cap at max-w-3xl, so this ternary collapses to
                  the single value below. Kept the cn() wrapper so future
                  conditional widths drop in cleanly. */}
              <div className={composerColumnClass}>
              <div className="flex flex-wrap gap-1.5">
                {lastMsg.options.map((opt, i) => (
                  <button
                    key={opt}
                    onClick={() => sendMessage(opt)}
                    disabled={isLoading}
                    className="inline-flex items-center cursor-pointer gap-2 border border-atrium-border rounded-lg px-3 py-1.5 text-sm bg-atrium-background hover:bg-atrium-content text-atrium-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="text-xs font-semibold text-atrium-black/60 shrink-0">{labels[i]}</span>
                    <span className="text-atrium-black font-medium">{opt}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-atrium-black/60 mt-2">or type your own answer</p>
              </div>
            </div>
          )
        }
        return null
      })()}

      {/* Input */}
      <div className={cn('pt-1 pb-3 shrink-0 relative z-30', mobileLayout && 'bg-atrium-chatbar pb-[calc(0.75rem+env(safe-area-inset-bottom))]')}>
        <div className={cn('flex items-center justify-between px-3 h-8 shrink-0 bg-linear-to-t/oklab absolute -top-8 left-0 right-0 z-0 pointer-events-none', shellFadeFromClass, shellFadeToClass)}></div>

        <div className={composerColumnClass}>
        <div className="relative z-[1]">
          {/* Brand field context quotes — shown above the prompt box */}
          {fieldContexts && fieldContexts.length > 0 && (
            <div className="mb-2">
              <div className="flex flex-col gap-1 rounded-xl bg-atrium-content border border-atrium-border px-3 py-2 text-xs text-atrium-black">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#939294]">
                    Quoting {fieldContexts.length} field{fieldContexts.length > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={onDismissFieldContexts}
                    className="text-[#939294] hover:text-atrium-black transition-colors"
                  >
                    <HugeiconsIcon icon={Cancel01Icon} className="size-3" />
                  </button>
                </div>
                {fieldContexts.map((ctx, fi) => (
                  <div key={fi} className="flex items-center gap-1.5">
                    <span className="font-medium capitalize">{ctx.field.replace(/_/g, ' ')}:</span>
                    {ctx.currentValue && (
                      <span className="truncate text-[#939294]">
                        {ctx.currentValue.length > 50 ? ctx.currentValue.slice(0, 50) + '…' : ctx.currentValue}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quoted text pills — shown above the prompt box */}
          {quotedTexts.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {quotedTexts.map((q, qi) => (
                <div key={qi} className="flex items-center gap-1 max-w-full px-2.5 py-1 bg-atrium-content rounded-lg text-xs text-atrium-black border border-atrium-border">
                  <span className="truncate max-w-[200px] italic">“{q.text}”</span>
                  <button
                    onClick={() => setQuotedTexts(prev => prev.filter((_, idx) => idx !== qi))}
                    className="shrink-0 text-[#939294] hover:text-atrium-black transition-colors ml-1"
                  >
                    <HugeiconsIcon icon={Cancel01Icon} className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <ChatPromptBox
            inputRef={inputRef}
            fileInputRef={fileInputRef}
            composerInputId={composerInputId}
            input={input}
            onInputChange={setInput}
            onKeyDown={handleInputKeyDown}
            placeholder={inputPlaceholder}
            minRows={2}
            maxRows={12}
            disabled={isLoading}
            hasContent={hasContent}
            isLoading={isLoading}
            onSend={() => sendMessage(input)}
            onStop={stopStreaming}
            onAttachClick={() => fileInputRef.current?.click()}
            onNewSession={startNewSession}
            onFileChange={handleFileSelect}
            accept={chatUploadAccept}
            attachDisabled={isUploading}
            attachDisabledTooltip={isUploading ? 'Uploading...' : undefined}
            attachments={pendingAttachments.map(a => ({
              id: a.id,
              name: a.file_name,
              size: a.file_size,
              mediaPreviewUrl: a.previewUrl ?? null,
              mediaKind: (a.file_type ?? '').startsWith('image/') ? 'image' as const
                : (a.file_type ?? '').startsWith('video/') ? 'video' as const
                : null,
            }))}
            onRemoveAttachment={removePendingAttachment}
            slashCommandContext={slashCommandContext}
            includeAdminSlashCommands={isAdmin}
            onSlashLLMRouted={(prompt) => void sendMessage(prompt)}
            onSlashNavigate={(path) => {
              navigateFromChat(path)
            }}
            onSlashUiAction={handleSlashUiAction}
            promptControls={promptControls}
          />
          {isUploading && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              <div className="flex items-center gap-1 px-2.5 py-1 bg-neutral-100 rounded-full text-xs text-atrium-muted">
                <span>Uploading…</span>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
      </>
      )}
    </div>
  )

  // ─── Variant rendering ───────────────────────────────────────────────────────

  if (variant === 'fullscreen') {
    return (
      <TooltipProvider delayDuration={0}>
        <div className={cn('flex flex-1 min-h-0 min-w-0 h-full', className)}>
          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            {renderWithSidebar(chatInner, 'w-64')}
          </div>
        </div>
      </TooltipProvider>
    )
  }

  if (variant === 'fullpage') {
    return (
      <TooltipProvider delayDuration={0}>
        <div className={cn("flex flex-1 min-h-0 overflow-hidden", className)}>
          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            {renderWithSidebar(chatInner, 'w-64')}
          </div>
          {rightPanel && (
            <div className="w-80 shrink-0 border-l border-atrium-border overflow-y-auto">
              {rightPanel}
            </div>
          )}
        </div>
      </TooltipProvider>
    )
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn('relative flex min-h-0 w-full min-w-0 flex-1 flex-col', className)}
      >
        <div
          className={cn('flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden', shellBgClass)}
        >
          {panelFrameTop}
          {isOffline ? (
            <div className="shrink-0 px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
              <span className="size-1.5 rounded-full bg-amber-600 animate-pulse" />
              You’re offline. Messages will resume when your connection is back.
            </div>
          ) : null}
          {renderWithSidebar(chatInner, 'w-56')}
        </div>
      </div>
    </TooltipProvider>
  )
}
